import React, { useState, useEffect, useRef, useMemo, useCallback, Fragment } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Backdrop,
  Skeleton,
  Typography,
  FormControl,
  MenuItem,
  IconButton,
  Pagination,
  Select,
  Alert,
  Link,
  PaginationItem,
  Tooltip,
  Breadcrumbs,
  Modal,
  useTheme,
} from '@mui/material';
import dummyMarketHistoricalData from 'src/App/assets/dummy_market_historical_data.json';
import _debounce from 'lodash/debounce';
import { FilterVal, VisibleListingsProp } from 'src/interfaces/iproptypes';
import {
  AVAILABLE_COUNTRY,
  COLOR_PRIMARY,
  exchangeRates,
  OverViewMapSectionMarketStatsTooltip,
  BEST_AIRBNB_MARKET_COUNTRY_CODE_MAP,
  ALL_COUNTRY,
  MAILCHIMP_FIELDS,
  DEFAULT_FILTER_VALUES,
  USER_WISE_SAVE_LIST_LIMIT,
  countryNameCodeMapFromDB,
  FREE_MARKET_SEARCH_LIMIT,
  SUBMARKET_SORTING_OPTIONS,
  RATE_LIMIT_ERROR_MESSAGE,
} from 'src/utils/const';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import MapIcon from '@mui/icons-material/Map';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import Map from 'src/App/Dashboards/Market/Components/Map';
import MainKPICard from './UtilityComponents/MainKPICard';
import './styles/styles.scss';
import {
  getClusters,
  getDistrictInfo,
  getHiddenSubmarkets,
  getMarketCachedDataWithFilters,
  getTopHosts,
  initializeSavedMarketsData,
  setIndividualRevenuePotentialRange,
  setMarketCachedDataWithFilters,
  setNoDataWithCurrentFilter,
  setSelectedListLoadingAction,
  toggleSubmarketVisibility,
  fetchCachedMarketData,
  updateMarketInList,
  getMarketGrade,
  updateUserMarketSummaryPDFExportUsageAction,
  setCachedMarketDataRateLimited,
  triggerListingsRefresh,
  setFiltersInsights,
} from 'src/store/Dashboard/actions';
import { useDispatch, useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import TabBar from 'src/App/Dashboards/Market/Components/Tabs/TabBar';
import {
  COLOR_PRIMARY_2,
  KPI_CARD_COLOR,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_700,
  COLOR_GREY_800,
} from 'src/utils/colors';
import { LoadingCard } from 'src/App/Dashboards/Market/Components/Cards';
import PropertySizeCard from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/PropertySizeCard';
import PropertyTypeChannelPieChart from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/PropertyTypeChannelPieChart';
import AmenityCard from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/AmenityCard';
import TopCitiesCard from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/TopCitiesCard';
import TopCountriesCard from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/TopCountriesCard';
import InternationDomesticGuestsCard from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/InternationDomesticGuestsCard';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import SubjectIcon from '@mui/icons-material/Subject';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MARKET_WIDE_BADGE_TOOLTIP } from './UtilityComponents/overviewSectionBadgeTooltips';
import AirbnbBadge from './UtilityComponents/AirbnbBadge';
import Hosts from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/Hosts';
import GuestLanguageCard from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/GuestLanguageCard';
import { callApi } from 'src/core/callApi';
import {
  convertCurrency,
  deepCopyObject,
  formatCurrency,
  getRedirectedDiscoveryMarketCountryCode,
  isEmpty,
  parseMessageHistoricalData,
  updateDynamoDBForUserVerification,
  checkUserTailorMarketAccess,
  changePercentageToDecimal,
  filterActiveListings,
  removeQueryParamsFromURL,
  roundNumber,
  getFilteredListings,
  getVisibleListings,
  titleCase,
  calculateHistoricalTrends,
  setCookie,
  getDynamicMarkers,
  isNoDataInMarket,
  setUserDataToMailChimp,
  getBestMarketBlogURLByCountry,
  parseFilterValToApiResponse,
  filterExampleSavedMarkets,
  abbreviateNumber,
  isMarketSearchAllowed,
  getSeasonality,
  getSeasonalityBarColor,
  getBookingMarkers,
  formatMarketName,
} from 'src/utils/functions';
import {
  processAndSortMarketLevelData,
  getHistoricalData,
  getMinmaxROI,
  getMarketKpiSelectedPercentileWise,
  getSavedClusters,
  saveFilterConfigInDynamodb,
  syncMarketCacheDataWithFilterData,
} from './helper';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import NoDataCard from 'src/App/Dashboards/Market/Components/Cards/NoDataCard';
import SingleBarFilterCard from 'src/App/Dashboards/Market/Components/FilterComponents/SingleBarFilterCard';
import ConfigurableFilterBarCard from 'src/App/Dashboards/Market/Components/FilterComponents/ConfigurableFilterBarCard';
import SimplifiedAirbnbPointerBody from 'src/App/Dashboards/Market/Components/Cards/SimplifiedAirbnbPointerBody';
import { VerifiedTag } from 'src/App/Dashboards/Market/Components/Tags';
import AIOverviewCard from 'src/App/Dashboards/Market/Components/Cards/AIOverviewCard';
import { BarGraphData, Bounds, Listing, Listings } from 'src/interfaces/idashboard';
import { infoIconButton, infoIconStyle } from 'src/App/Dashboards/Market/styles/style';
import { LTMMonthlyPerformanceBreakdownType } from 'src/interfaces/apiTypes';
import { IndividualListingModalV2 } from 'src/App/Dashboards/Market/Modals';
import { useParams } from 'react-router-dom';
import { ListingsExportModal } from 'src/App/Dashboards/Market/Modals/SubscriptionAddOns';
import { PaymentFulfillmentCheckModal } from 'src/App/Dashboards/Market/Components/Modals';
import SeasonalityChart from './UtilityComponents/SeasonalityChart';
import TrendsChart from './UtilityComponents/TrendsChart';
import AmenitiesImpactOnRevenueChart from './UtilityComponents/AmenitiesImpactOnRevenueChart';
import SubmarketCreationModal from 'src/App/Dashboards/Market/Modals/SubmarketCreationModal/SubmarketCreationModal';
import {
  InfoButton,
  TooltipedActionButton,
  ViewDemoButton,
} from 'src/App/Dashboards/Market/Components/Buttons';
import ActionOptions from './UtilityComponents/ActionOptions';
import { ListingsGridLoadingCard } from 'src/App/Dashboards/Market/Components/Loaders';
import { useIsDisabled } from 'src/hooks/useFeatureAccess';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import TutorialModal from 'src/App/Dashboards/Market/Modals/TutorialModal';
import { logAmplitudeEvent } from 'src/utils/analytics';
import ShowAlertTextBox from 'src/App/Dashboards/Market/Components/ShowAlertTextBox';
import MainKPITooltip from 'src/App/Dashboards/Market/Components/Tooltips/MainKPITooltip';
import { kpiTooltipConfig } from './configs/tooltipConfig';
import SubmarketsTab from 'src/App/Dashboards/Market/Components/SubMarketsTab/SubmarketsTab';
import DailyOccupancyRateCalendar from 'src/App/Dashboards/Market/Components/DailyOccupancyRateCalendar/DailyOccupancyRateCalendar';
import FloatingClusterButton from './UtilityComponents/FloatingClusterButton';
import ListingCardErrorBoundary from 'src/App/Dashboards/Market/Components/ErrorBoundary/ListingCardErrorBoundary';
import FilterBar from 'src/App/Dashboards/Market/Components/Filters/FilterBar';
import { useVersionedCallApi } from 'src/hooks/useCustomHooks';
import SubmarketToTailoredRegion from 'src/App/Dashboards/Market/Components/SubmarketToTailoredRegion';
import MarketGradeAIOverview from './UtilityComponents/MarketGradeAIOverview';
import FullScreenLoader from 'src/App/Dashboards/Market/Components/Loaders/FullScreenAnimationLoader';
import SubmarketCTA from 'src/App/Dashboards/Market/Components/SubMarketsTab/SubmarketCTA';
import MarketIntelligenceTab from './UtilityComponents/MarketIntelligenceTab';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getSubmarketNameById } from 'src/App/Dashboards/Market/Components/SubMarketsTab/helper';
import ExportMarketPdfModal from 'src/App/Dashboards/Market/Components/ExportMarketPDF/ExportMarketPdfModal';
import PDFDownloadModal from './UtilityComponents/PDFDownloadModal';
import GraphExportModal from './UtilityComponents/GraphExportModal';
import BestPropertyTypeOverview from './UtilityComponents/BestPropertyTypeOverview';
import RealtorForm from '../EstimatorPage/UtilityComponents/RealtorForm/RealtorForm';
import ExplorerPagePage from '../ExplorerPage/ExplorerPage';
import RateLimitAlert from '../../Components/Alerts/RateLimitAlert';

const OVERVIEW_PAGE_STICKY_SUBSECTION_NAVBAR_THRESHOLD = 60;

const MAP_SIZE_MAP_VIEW_DESKTOP = 75;
const MAP_SIZE_MAP_VIEW_COLLAPSED_DESKTOP = 40;

const redirectToDefaultRegionIfNeeded = (loginCountryCode, dispatch, pathname, dummyMarket) => {
  if (pathname === '/airbnb-data' || pathname === '/airbnb-data/') {
    const redirectTo = `/airbnb-data/${dummyMarket['country']}/${dummyMarket['city']}/${dummyMarket['district']}?market_id=${dummyMarket['id']}`;
    window.history.pushState({}, '', redirectTo);
    dispatch(
      getDistrictInfo(
        loginCountryCode,
        dummyMarket['district'],
        dummyMarket['city'],
        dummyMarket['country']
      )
    );
  }
};

const getSelectedViewWidth = (
  view: 'map' | 'dashboard' | 'compare' | 'searchListing' | 'submarkets',
  matches: boolean
) => {
  if (matches) {
    return view == 'map' ? 100 : 0;
  } else {
    if (view == 'map') {
      return MAP_SIZE_MAP_VIEW_COLLAPSED_DESKTOP;
    } else if (view == 'submarkets') {
      return 0;
    } else if (view == 'compare') {
      return 0;
    } else {
      return 20;
    }
  }
};

const getMapViewHeadings = (
  text1: string,
  text2: string,
  infoTooltipContent: string | undefined = undefined
) => {
  return (
    <Grid
      item
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography
        variant="h5"
        className="marketTitle"
        sx={{
          fontSize: '20px',
          fontWeight: 'bold',
          position: 'relative',
          top: '5px',
        }}
      >
        {text1}: <span style={{ marginRight: '5px' }}>{text2}</span>
        {infoTooltipContent && (
          <TooltipNew
            title={''}
            content={infoTooltipContent}
            enterTouchDelay={0}
            leaveTouchDelay={6000}
          >
            <IconButton style={infoIconButton}>
              <InfoOutlinedIcon style={infoIconStyle} />
            </IconButton>
          </TooltipNew>
        )}
      </Typography>
    </Grid>
  );
};

const OverviewPage = ({
  search,
  inputMarketName,
  SEODistrictName,
  SEOCountryName,
  SEOCityName,
  isLoggedIn,
  dashboardLayoutView,
  setDashboardLayoutView,
  matches,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  currency,
  user,
  setShowSearchCreditDeductWarningModal,
  handleOnSampleClick,
  handleMarketSidebarClick,
  handleSidebarListItemClick,
  openDemoBookingForm,
  setDemoFormHeadingFor,
  dummyMarket,
  loginCountryCode,
  dataStoreTimePeriodQuery,
  dataStoreTimeGranularityQuery,
  getTokenThunk,
  tailoredRegionMarkers,
  getTailoredRegionMarkers,
  OnBoardingSearchBox,
  cachedMarketData,
  getEstimatorDashboardComponent,
  isTailoredRegionOpen,
  setIsTailoredRegionOpen,
  setLoginModalOpen,
}) => {
  let { country, city, district } = useParams<{
    country: string;
    city: string;
    district: string;
  }>();

  const marketIDFromURL = new URLSearchParams(window.location.search).get('market_id');

  const dispatch = useDispatch();
  const theme = useTheme();

  const marketID = marketIDFromURL || search.data?.id;
  const userCountryCode = useSelector(state => (state as any).dashboard.userCountryCode);
  const priceTiers = useSelector(state => (state as any).dashboard.priceTiers);
  const isCachedMarketDataSample: boolean = useSelector(
    state => (state as any).dashboard.isCachedMarketDataSample
  );
  const [isMarketOverviewLoading, setIsMarketOverviewLoading] = useState<boolean>(true);
  const [showTutorial, setShowTutorial] = useState<boolean>(false);

  const savedMarketsState = useSelector((state: any) => state.dashboard.savedMarkets);
  const {
    data: selectedList,
    loading: selectedListLoading,
    isError: selectedListError,
  } = savedMarketsState;

  const [userRemovedThisMarketFromOverview, setUserRemovedThisMarketFromOverview] = useState<
    boolean
  >(false);

  const mobileHeaderRef = useRef<HTMLDivElement>(null);
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState<number>(150);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (mobileHeaderRef.current && matches) {
        const height = mobileHeaderRef.current.offsetHeight;
        setMobileHeaderHeight(height);
      }
    };

    if (!matches) {
      setMobileHeaderHeight(0);
      return;
    }

    updateHeaderHeight();

    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    if (mobileHeaderRef.current) {
      resizeObserver.observe(mobileHeaderRef.current);
    }

    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, [matches, dashboardLayoutView, isMarketOverviewLoading]);

  useEffect(() => {
    if (country && city && district) {
      const countryCode = countryNameCodeMapFromDB[country] || null;

      if (!marketIDFromURL) {
        // call market grade and market data cache after we get the district info
        dispatch(getDistrictInfo(loginCountryCode, district, city, country, true, true, null));
      } else {
        // queue all the api calls asynchronously to save time
        dispatch(getMarketGrade(parseInt(marketIDFromURL)));
        dispatch(
          getDistrictInfo(
            loginCountryCode,
            district,
            city,
            country,
            false,
            marketIDFromURL ? false : true,
            marketIDFromURL ? marketIDFromURL : null
          )
        );
        dispatch(
          fetchCachedMarketData(
            marketIDFromURL,
            district,
            city,
            countryCode,
            country,
            loginCountryCode
          )
        );
      }
    }
  }, []);

  useEffect(() => {
    setIsMarketOverviewLoading(search.loading);

    // Clear the graph refs when a new market is loading
    if (search.loading) {
      dailyOccupancyRateCalendarHistoricalGraphRef.current = null;
      dailyOccupancyRateCalendarFutureGraphRef.current = null;
    }
  }, [search]);

  useEffect(() => {
    if (!search.loading && search.data && ['T1', 'T2', 'T3', 'S'].includes(search.data.tier)) {
      setShouldShowDailyOccupancyRateCalendar(true);
    } else {
      setShouldShowDailyOccupancyRateCalendar(false);
    }
  }, [search]);

  useEffect(() => {
    if (search.data.id) {
      marketToSaveTryCount.current = 0;
    }
  }, [search.data]);

  const marketToSaveTryCount = useRef<number>(0);

  const handleRequestHardRefresh = useCallback(() => {
    const marketId = marketIDFromURL || search.data?.id;
    if (!marketId || !district || !city || !country) return;
    const countryCode = countryNameCodeMapFromDB[country] || '';
    const isVerifiedMarket = (search.data as any)?.isVerifiedMarket ?? false;
    (dispatch(
      triggerListingsRefresh(
        String(marketId),
        district,
        city,
        countryCode,
        country,
        loginCountryCode,
        isVerifiedMarket
      )
    ) as unknown as Promise<{ queued?: boolean }>).then(result => {
      if (result?.queued) {
        toast.success('Refresh started. Reload the page in a few minutes to see updated data.');
      }
    });
  }, [
    marketIDFromURL,
    search.data,
    district,
    city,
    country,
    loginCountryCode,
    dispatch,
  ]);

  useEffect(() => {
    if (
      isLoggedIn &&
      !selectedListLoading &&
      !selectedListError &&
      selectedList &&
      marketID &&
      selectedList.marketIDs &&
      !selectedList.marketIDs[marketID] &&
      isLoggedIn &&
      !userRemovedThisMarketFromOverview &&
      USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan] >
        filterExampleSavedMarkets(selectedList.marketDetails).length
    ) {
      marketToSaveTryCount.current++;
      if (marketToSaveTryCount.current > 3) {
        return;
      }
      dispatch(updateMarketInList(selectedList, Number(marketID), 'add', search.data.name));
    }
  }, [selectedList]);

  const revPotentialRangeInvestment = cachedMarketData.data?.revenue_potential_tier_distribution;
  const filteredListingsDashboard = useSelector(
    state => (state as any).dashboard.filteredListingsDashboard
  );
  const isUserAllowed = useSelector(state => (state as any).dashboard.isUserAllowed);
  const cachedSearchMarkets = useSelector(state => (state as any).dashboard.cachedSearchMarkets);

  const isDemoMarket = search.data.name.toLowerCase() === dummyMarket.district.toLowerCase();
  const listingsExportPaymentFailed = new URLSearchParams(window.location.search).get(
    'listings_export_payment_failed'
  );
  const listingsExportSessionID = new URLSearchParams(window.location.search).get(
    'listings_export_session_id'
  );
  const visibleMapSupply = useSelector(state => (state as any).dashboard.visibleMapSupply);

  const topHosts = useSelector(state => (state as any).dashboard.topHosts);
  const hiddenSubmarkets = useSelector((state: any) => state.dashboard.hiddenSubmarkets);
  const filtersInsights = useSelector((state: any) => state.dashboard.filtersInsights);
  const mapOverviewKpi = useSelector((state: any) => state.dashboard.mapOverviewKpi);

  redirectToDefaultRegionIfNeeded(
    loginCountryCode,
    dispatch,
    window.location.pathname,
    dummyMarket
  );

  const userTailorMarketAccess = checkUserTailorMarketAccess(user, search);

  const isDisabled: boolean = useIsDisabled();
  const [mapWidth, setMapWidth] = useState<0 | 20 | 40 | 75 | 100>(
    getSelectedViewWidth('dashboard', matches)
  );
  const [areCriticalDataLoaded, setAreCriticalDataLoaded] = useState(false);

  const [visibleMapListings, setVisibleMapListings] = useState<Listing[]>([]);
  const [visibleBookingListings, setVisibleBookingListings] = useState<any[]>([]);
  const [marketsInMapViewport, setMarketsInMapViewport] = useState<any[]>([]);
  const [mapViewportListings, setMapViewportListings] = useState<{
    count: number;
    loading: boolean;
  }>({
    count: 0,
    loading: true,
  });

  const [isDynamicDataInitialized, setIsDynamicDataInitialized] = useState(false);
  const [isDynamicDataBtnVisible, setIsDynamicDataBtnVisible] = useState(false);
  const [dashboardScrollPositionTop, setDashboardScrollPositionTop] = useState<number>(0);
  const [subKpiSelected, setSubKpiSelected] = useState<string>('revenue');
  const [subKpiMapViewSelected, setSubKpiMapViewSelected] = useState<string>('listings');
  const [selectedPercentileInMainKPICard, setSelectedPercentileInMainKPICard] = useState<string>(
    '50'
  );

  const [extraExportCreditsLoading, setExtraExportCreditsLoading] = useState<boolean>(false);
  const [extraExportError, setExtraExportError] = useState<boolean>(false);
  const [creditsToBeDeducted, setCreditsToBeDeducted] = useState<number>(1);

  const [marketLevelOccupancyRate, setMarketLevelOccupancyRate] = useState<number>(0);
  const [marketLevelRevenuePotential, setMarketLevelRevenuePotential] = useState<number>(0);
  const [marketLevelCleaningFee, setMarketLevelCleaningFee] = useState<number>(0);
  const [marketLevelBookingCount, setMarketLevelBookingCount] = useState<number>(0);
  const [marketLevelDailyRate, setMarketLevelDailyRate] = useState<number>(0);
  const [marketLevelActiveListings, setMarketLevelActiveListings] = useState<number>(0);
  const [isDiscoveryMapViewOpen, setIsDiscoveryMapViewOpen] = useState<boolean>(true);
  const [occupancyRateHistoricalData, setOccupancyRateHistoricalData] = useState<any>([]);
  const [revenueHistoricalData, setRevenueHistorical] = useState<any>([]);
  const [dailyRateHistoricalData, setDailyRateHistorical] = useState<any>([]);
  const [listingHistoricalData, setListingHistorical] = useState<any>([]);
  const [occupancyPast6MonthsHistoricalData, setOccupancyPast6MonthsHistorical] = useState<any>([]);
  const [revenuePast6MonthsHistoricalData, setRevenuePast6MonthsHistorical] = useState<any>([]);
  const [dailyRatePast6MonthsHistoricalData, setDailyRatePast6MonthsHistorical] = useState<any>([]);
  const [listingPast6MonthsHistoricalData, setListingPast6MonthsHistorical] = useState<any>([]);
  const [occupancyYoYChange, setOccupancyYoYChange] = useState<Number>();
  const [revenueYoYChange, setRevenueYoYChange] = useState<Number>();
  const [dailyYoYChange, setDailyYoYChange] = useState<Number>();
  const [listingYoYChange, setListingYoYChange] = useState<Number>();
  const [isMapViewMainKpiCardTooltipOpen, setIsMapViewMainKpiCardTooltipOpen] = useState<boolean[]>(
    [false, false, false, false]
  ); //occupancy, revenue, nightly, listings

  const [isScoreCardTooltipOpen, setIsScoreCardTooltipOpen] = useState<boolean>(false);
  const [isMarketCachedDataLoaded, setIsMarketCachedDataLoaded] = useState<boolean>(false);
  const [cachedHistoricalData, setCachedHistoricalData] = useState<{
    [key: string]: { [key: string]: number };
  } | null>(null);
  const [cachedMarketBedroomADR, setCachedMarketBedroomADR] = useState<any>(null);
  const [cachedLTMData, setCachedLTMData] = useState<{ [key: string]: number } | null>(null);
  const [
    cachedMonthlyLTMData,
    setCachedMonthlyLTMData,
  ] = useState<LTMMonthlyPerformanceBreakdownType | null>(null);

  const [showAddAirbnbURLModal, setShowAddAirbnbURLModal] = useState<boolean>(false);
  const [userSeachedListingManually, setUserSeachedListingManually] = useState<boolean>(false);
  const [
    confirmSaveCountryPlanMarketModalOpen,
    setConfirmSaveCountryPlanMarketModalOpen,
  ] = useState<boolean | undefined>(false);

  const [showPremiumDataDynamic, setShowPremiumDataDynamic] = useState<boolean>(true);

  const [topPerformingListingOnFocus, setTopPerformingListingOnFocus] = useState<string | null>(
    null
  );

  const [listingGridPage, setListingGridPage] = useState<number>(1);
  const [listingsGridStartIndexOffset, setListingsGridStartIndexOffset] = useState<number>(0);
  const [topPerformingListingModalOpen, setTopPerformingListingModalOpen] = useState<
    Listing | null | any[]
  >(null);

  const [discoveryMarkets, setDiscoveryMarkets] = useState<any[] | null | undefined>([]);
  const [minROI, setMinROI] = useState(0);
  const [maxROI, setmaxROI] = useState(0);

  const [isTabChanging, setIsTabChanging] = useState<boolean>(false);
  const [showDiscoveryAI, setShowDiscoveryAI] = useState<boolean>(false);

  const [isDiscoveryMarketLoading, setIsDiscoveryMarketLoading] = useState<boolean>(true);

  const [marketsToCompareList, setMarketsToCompareList] = useState<any[]>([]);

  const [discoverTabData, setDiscoveryTabData] = useState<any>([]);

  const [topRawMarketData, setTopRawMarketData] = useState<any[]>([]);
  const [allRawMarketData, setAllRawMarketData] = useState<any[]>([]);
  const [discoveryMarketType, setDiscoveryMarketType] = useState<'all' | 'top'>('all');
  const [processedMarketData, setProcessedMarketData] = useState<any[]>([]);
  const [allMarketProcessedData, setAllMarketProcessedData] = useState<any[]>([]);
  const [isMarketDataAccessible, setIsMarketDataAccessible] = useState<boolean>(false);
  const [freeTopMarketData, setFreeTopMarketData] = useState<any[]>([]);
  const [freeAllMarketData, setFreeAllMarketData] = useState<any[]>([]);
  const [freeMarketDataKey, setFreeMarketDataKey] = useState<string[]>([]);
  const subKPIcontainerRef = useRef<HTMLDivElement | null>(null);
  const [showListingsExportPage, setShowListingsExportPage] = useState<boolean>(false);
  const subKPIcontainerMapViewRef = useRef<HTMLDivElement | null>(null);
  const warningToastRef = useRef<React.ReactText | undefined>(undefined);
  const [listingSortingKey, setListingSortingKey] = useState<string>('top_properties');
  const [bounds, setBounds] = useState<Bounds>({} as Bounds);
  const [showSubmarketCreationModal, setShowSubmarketCreationModal] = useState(false);
  const [submarketCreationSource, setSubmarketCreationSource] = useState<'submarkets-tab' | 'map'>(
    'map'
  );
  const [hoveredSubmarketId, setHoveredSubmarketId] = useState<string | null>(null);
  const [newlyAddedSubmarketId, setNewlyAddedSubmarketId] = useState<string | null>(null);
  const [isNewSubmarketDrawingMode, setIsNewSubmarketDrawingMode] = useState<boolean>(false);
  const formattedSubmarketSummary = useRef<any>(null);
  const top3SubmarketsRef = useRef<{
    chartData: any;
    summary: any;
  }>({
    chartData: null,
    summary: null,
  });
  const isFilterGraphRequestInFlightRef = useRef<boolean>(false);

  const [tailoredSubmarketPolygon, setTailoredSubmarketPolygon] = useState<string | null>(null);
  const [showExportAsPDFModal, setShowExportAsPDFModal] = useState<boolean>(false);
  const [isPdfDownloading, setIsPdfDownloading] = useState<boolean>(false);
  const [downloadPDFUrl, setDownloadPDFUrl] = useState<string | null>(null);
  const [shouldShowDailyOccupancyRateCalendar, setShouldShowDailyOccupancyRateCalendar] = useState<
    boolean
  >(false);

  const isUSMarket =
    search.data && search.data.countryCode && search.data.countryCode === 'US' ? true : false;

  const [realtorModalOpen, setRealtorModalOpen] = useState<boolean>(false);

  const handleExportDashboardPDF = async (requestBody: any) => {
    setIsPdfDownloading(true);
    const URL =
      'https://gwjkol1l8h.execute-api.us-east-2.amazonaws.com/prod/generateMarketSummaryPDF';
    const response = await callApi(URL, { body: requestBody }, true, false, '', 'POST', true);
    console.log('PDF Res =>', response);
    if (response && response.pdf_url) {
      dispatch(updateUserMarketSummaryPDFExportUsageAction(response.credits_used));
      setDownloadPDFUrl(response.pdf_url);
    }
    setIsPdfDownloading(false);
  };

  // Why use toastify here?
  // MUI notification don't support notification stacking and requires another library to be installed.
  // We already have toastify installed and creating notification with this is simple and it works out of the box.
  const createSubmarketHideUnhideNotification = (
    message: string,
    severity: 'success' | 'error'
  ) => {
    const config: any = {
      position: 'bottom-left',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'light',
    };

    severity === 'success' ? toast.success(message, config) : toast.error(message, config);
  };

  const [selectedDiscoveryCountry, setSelectedDiscoveryCountry] = useState<string>(
    country && Object.keys(AVAILABLE_COUNTRY).includes(country.toUpperCase())
      ? country.toUpperCase()
      : ''
  );

  const [filteredDiscoveryMarketsSet, setFilteredDiscoveryMarketsSet] = useState<Set<string>>(
    new Set<string>()
  );

  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({
    lat: search.data.lat,
    lng: search.data.lng,
  });

  const [heatmapMode, setHeatmapMode] = useState<'smartZones' | 'submarkets'>('submarkets');

  const [mapMode, setMapMode] = useState<'clusters' | 'heatmap' | 'listings'>(
    dashboardLayoutView == 'submarkets' ? 'heatmap' : 'listings'
  );
  const [showFullSubmarketMap, setShowFullSubmarketMap] = useState<boolean>(false);

  const cachedMarketDataWithoutFilters = useSelector(
    state => (state as any).dashboard.cachedMarketDataWithoutFilters
  );

  const noDataWithCurrentFilter = useSelector(
    state => (state as any).dashboard.noDataWithCurrentFilter
  );

  const noDataInMarket =
    noDataWithCurrentFilter ||
    (!cachedMarketData.loading && isNoDataInMarket(cachedMarketData.data));

  const noDataInMarketAlert = !search.loading && search.data?.isSubmarket && noDataInMarket && (
    <Grid item xs={12} sx={{}}>
      <Alert severity="error">
        <span style={{ fontWeight: 'bold' }}>Limited Data Coverage:</span> The selected submarket
        may not contain sufficient data for a fully reliable analysis. Expanding the area or making
        the filter less specific may improve data accuracy and completeness.
      </Alert>
    </Grid>
  );

  const [
    showListingsExportPaymentFulfillmentCheckModal,
    setShowListingsExportPaymentFulfillmentCheckModal,
  ] = useState(false);

  const [listingsExportCheckoutStatus, setListingsExportCheckoutStatus] = useState('loading');
  const [filterApplied, setFilterApplied] = useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [topPerformingMarketOnFocus, setTopPerformingMarketOnFocus] = useState<any | null>(null);
  const [dynamicTrigger, setDynamicTrigger] = useState(false);
  const [zoom, setZoom] = useState<number>(12);

  const [mapCopy, setMapCopy] = useState<any>(null);
  const [mapsCopy, setMapsCopy] = useState<any>(null);

  const [savedClusters, setSavedClusters] = useState<any[]>([]);

  const dynamicLoadingMapViewKPI =
    (dashboardLayoutView === 'map' || dashboardLayoutView === 'dashboard') && dynamicTrigger;

  const [filterVal, setFilterVal] = useState(deepCopyObject(filtersInsights) as FilterVal);

  useEffect(() => {
    setFilterVal(deepCopyObject(filtersInsights) as FilterVal);
  }, [filtersInsights]);

  const [filterActive, setFilterActive] = useState(false);
  const [submarketPolygonPaths, setSubmarketPolygonPaths] = useState([]);
  const [boundarySelectionModalOpen, setBoundarySelectionModalOpen] = useState(true);
  const [lastYearMarketTrend, setLastYearMarketTrend] = useState<number | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [showViewingLisingFromPaginationAlert, setShowViewingLisingFromPaginationAlert] = useState<
    'shown' | 'showing' | null
  >(null);

  const [mapHeightOnMobileView, setMapHeightOnMobileView] = useState<number>(40);

  const [submarkets, setSubmarkets] = useState<any>('loading');
  const [visibleSubmarkets, setVisibleSubmarkets] = useState<any[]>([]);
  const [isSubmarketAccessAllowed, setIsSubmarketAccessAllowed] = useState<boolean>(false);
  const [submarketSortKey, setSubmarketSortKey] = useState<string>('');
  const [submarketBedroomType, setSubmarketBedroomType] = useState<string | null>(null);
  const [submarketSummary, setSubmarketSummary] = useState<any>(null);
  const [submarketParentMarketPerformance, setSubmarketParentMarketPerformance] = useState<any>(
    null
  );
  const [showSubmarketTutorial, setShowSubmarketTutorial] = useState<boolean>(false);
  const [showSubmarketMobileLegend, setShowSubmarketMobileLegend] = useState<boolean>(false);

  const [isSubmarketRateLimited, setIsSubmarketRateLimited] = useState<boolean>(false);

  const parentMarketID = search.data?.parentMarketID;

  const [parentMarketDetails, setParentMarketDetails] = useState<{
    name: string;
    city: string;
    country: string;
    displayName: string | null;
    id: string | number | null;
    polygon: string | null;
    multiPolygon: string | null;
    boundary: string | null;
  } | null>(null);
  const [shouldShowVerifiedSubmarkets, setShouldShowVerifiedSubmarkets] = useState<boolean>(false);

  const [currentHoveredCardFromDataGrid, setCurrentHoveredCardFromDataGrid] = useState<
    string | null
  >(null);

  const [filterCacheLoading, setFilterCacheLoading] = useState<boolean>(false);

  const [marketTotalListingsCount, setMarketTotalListingsCount] = useState<{
    loading: boolean;
    data: number | null;
  }>({
    loading: true,
    data: null,
  });

  const marketGrade = useSelector((state: any) => state.dashboard.marketGrade);
  const isMapListingsRateLimited = useSelector((state: any) => state.dashboard.mapListings);

  useEffect(() => {
    if (isMapListingsRateLimited.isRateLimited) {
      createSubmarketHideUnhideNotification(RATE_LIMIT_ERROR_MESSAGE, 'error');
    }
  }, [isMapListingsRateLimited.isRateLimited]);

  const { versionedCallApi } = useVersionedCallApi();

  const fetchMarketTotalListingsCount = async () => {
    setMarketTotalListingsCount({ loading: true, data: null });
    const url = 'https://xnxq16nesj.execute-api.us-east-2.amazonaws.com/default/getMapListings';
    const requestBody = {
      market_id: search.data.id,
      return_supply: true,
      marketPolygon: search.data.polygon || search.data.multiPolygon,
    };

    const { filters } =
      filterCacheLoading || search.data.isSubmarket || showFilterComingSoonAlert || !isUserAllowed
        ? { filters: {} }
        : parseFilterValToApiResponse(filterVal);

    if (!isEmpty(filters)) {
      requestBody['filters'] = filters;
    }

    try {
      const response = await callApi(
        url,
        { body: requestBody, queryParams: { apiType: 'v2' } },
        true,
        false,
        '',
        'POST',
        true,
        undefined,
        true,
        true
      );

      if (response && response.status == 429) {
        setMarketTotalListingsCount({ loading: false, data: null });
      }

      if (response && response.message) {
        const supply = response.message.supply;
        setMarketTotalListingsCount({ loading: false, data: supply });
      }
    } catch (error) {
      console.error('Error fetching dynamic supply:', error);
      setMarketTotalListingsCount({ loading: false, data: null });
    }
  };

  useEffect(() => {
    if (
      cachedMarketData.data &&
      (!marketGrade.loading || marketGrade.isError || marketGrade.is404Error)
    ) {
      setAreCriticalDataLoaded(true);
    }
  }, [cachedMarketData, marketGrade]);

  useEffect(() => {
    /*
      If less than 3 submarkets, show smartzones by default in the submarket heatmap.
    */
    if (submarkets.length < 3) {
      setHeatmapMode('smartZones');
    } else {
      setHeatmapMode('submarkets');
    }
  }, [submarkets]);

  const updateMailChimpData = () => {
    const marketURL = 'https://app.airbtics.com' + window.location.pathname;
    const marketName = formatMarketName(search.data);
    const countryCode = search.data.countryCode;

    const bestMarketCountryName = BEST_AIRBNB_MARKET_COUNTRY_CODE_MAP[countryCode];
    const countryName = bestMarketCountryName || ALL_COUNTRY[countryCode];
    const bestMarketBlogURL = bestMarketCountryName
      ? getBestMarketBlogURLByCountry(countryName)
      : '';

    const mailchimpFields: [string, string | boolean][] = [
      [MAILCHIMP_FIELDS.LATEST_INTERESTED_COUNTRY, countryName],
      [MAILCHIMP_FIELDS.LATEST_INTERESTED_MARKET, marketName],
      [MAILCHIMP_FIELDS.LATEST_INTERESTED_MARKET_APP_URL, marketURL],
      [MAILCHIMP_FIELDS.LATEST_INTERESTED_COUNTRY_BEST_MARKET_BLOG_URL, bestMarketBlogURL],
    ];

    setUserDataToMailChimp(mailchimpFields);
  };

  useEffect(() => {
    const fetchInOrder = async () => {
      setSubmarkets('loading');
      await fetchSubMarketData();
      await fetchMarketTotalListingsCount();

      dispatch(
        getTopHosts(search.data.parentMarketID ? search.data.parentMarketID : search.data.id)
      );
      dispatch(getClusters(search.data.id.toString()));

      dispatch(initializeSavedMarketsData());
      if (!isLoggedIn) {
        dispatch(setSelectedListLoadingAction(false));
      }

      dispatch(getHiddenSubmarkets(parentMarketID ? parentMarketID : search.data.id));
      updateMailChimpData();
    };
    areCriticalDataLoaded && !search.loading && search.data.id && fetchInOrder();
  }, [areCriticalDataLoaded, search]);

  useEffect(() => {
    if (search.data.id) {
      fetchMarketTotalListingsCount();
    }
  }, [search.data.id, filterVal]);

  /*
    Whenever filter change, this function is called.
  */
  const debouncedFilterEffect = useCallback(
    _debounce(async (currentFilterVal, currentFilterActive) => {
      dispatch(setFiltersInsights(currentFilterVal));
      isLoggedIn && saveFilterConfigInDynamodb(currentFilterVal);
      dispatch(setCachedMarketDataRateLimited(false));

      if (!isUserAllowed && !isDemoMarket) {
        cachedMarketDataWithoutFilters &&
          dispatch(setMarketCachedDataWithFilters(cachedMarketDataWithoutFilters));
        return;
      }

      const { filters } = parseFilterValToApiResponse(currentFilterVal);

      dispatch(getMarketCachedDataWithFilters());
      /*
        Trigger this if there's items in the filters
      */
      if (Object.keys(filters).length > 0) {
        if (!currentFilterActive) {
          setFilterActive(true);
        }

        isFilterGraphRequestInFlightRef.current = true;
        versionedCallApi(
          'https://rdqzy30d3g.execute-api.us-east-2.amazonaws.com/default/getMarketDashboardGraphData',
          {
            body: {
              market_id: search.data.id,
              filters: filters,
            },
            queryParams: {
              apiType: 'v2',
            },
          },
          true,
          false,
          '',
          'POST',
          true,
          async response => {
            const message = response?.message;
            const isError = response?.error || message?.error;
            let data = message?.data;

            if (data && !isEmpty(data) && !isError) {
              const filterAppliedMarketData = syncMarketCacheDataWithFilterData(
                cachedMarketDataWithoutFilters,
                response.message.data
              );

              filterAppliedMarketData &&
                !isEmpty(filterAppliedMarketData) &&
                dispatch(setMarketCachedDataWithFilters(filterAppliedMarketData));
            } else {
              /*
                This block is triggered if there is no results in the API.
              */
              dispatch(setMarketCachedDataWithFilters(cachedMarketDataWithoutFilters)); 
              dispatch(setNoDataWithCurrentFilter(true));
            }
            setFilterCacheLoading(false);
            isFilterGraphRequestInFlightRef.current = false;
          },
          true,
          () => {
            setFilterCacheLoading(false);
            isFilterGraphRequestInFlightRef.current = false;
            dispatch(setCachedMarketDataRateLimited(true));
          }
        );
      } else {
        cachedMarketDataWithoutFilters &&
          dispatch(setMarketCachedDataWithFilters(cachedMarketDataWithoutFilters));
      }
    }, 300),
    [
      search.data,
      filterApplied,
      cachedMarketDataWithoutFilters,
      isUserAllowed,
      isDemoMarket,
      filterVal,
      bounds,
      listingSortingKey,
    ]
  );

  const fetchFilteredData = () => {
    if (!cachedMarketDataWithoutFilters) {
      return false;
    }

    const marketTotalListingSupply = cachedMarketDataWithoutFilters?.ltm_performance?.supply;

    if (!marketTotalListingSupply) {
      return false;
    }

    debouncedFilterEffect(filterVal, filterActive);

    return true;
  };

  useEffect(() => {
    areCriticalDataLoaded && fetchFilteredData();

    return () => {
      debouncedFilterEffect.cancel();
    };
  }, [filterVal, cachedMarketDataWithoutFilters, search.data, areCriticalDataLoaded]);

  useEffect(() => {
    let interval: any = null;

    if (filterCacheLoading) {
      interval = setInterval(() => {
        if (isFilterGraphRequestInFlightRef.current) return;
        fetchFilteredData();
      }, 3000);
    }

    return () => {
      clearInterval(interval);
      debouncedFilterEffect.cancel();
    };
  }, [filterCacheLoading]);

  useEffect(() => {
    setSavedClusters(getSavedClusters(marketID));
  }, [marketID]);

  const currentMarketUrl = useRef<string>(
    window.location.pathname.includes('/markets/compare')
      ? `/airbnb-data/${dummyMarket['country']}/${dummyMarket['city']}/${dummyMarket['district']}`
      : window.location.pathname
  );

  const marketDisplayName = search.loading
    ? titleCase(inputMarketName)
    : formatMarketName(search.data);
  const parentMarketDisplayName = parentMarketDetails
    ? titleCase(parentMarketDetails.displayName || parentMarketDetails.name)
    : '';
  const isMapCollapsed = mapWidth === MAP_SIZE_MAP_VIEW_COLLAPSED_DESKTOP;

  const handleHideSubmarket = async (marketID: string) => {
    try {
      // Then dispatch the action to update the backend
      await dispatch(
        toggleSubmarketVisibility(
          parentMarketID ? parentMarketID : search.data.id,
          [marketID],
          'hide'
        )
      );

      // Refresh the hidden submarkets list after successful hide
      // dispatch(getHiddenSubmarkets(parentMarketID ? parentMarketID : search.data.id));
      createSubmarketHideUnhideNotification(
        `${getSubmarketNameById(marketID, submarkets)} was hidden successfully.`,
        'success'
      );
    } catch (error) {
      console.error('Error hiding submarket:', error);
      // Revert the local state if the API call fails
      setVisibleSubmarkets(prevSubmarkets => {
        const submarketToRestore = submarkets.find(s => s.submarket_id == marketID);
        return submarketToRestore ? [...prevSubmarkets, submarketToRestore] : prevSubmarkets;
      });
      createSubmarketHideUnhideNotification(
        `Failed to hide ${getSubmarketNameById(marketID, submarkets)}.`,
        'error'
      );
    }
  };

  useEffect(() => {
    if (!search.loading) {
      // Store last market searched in a cross sub domain cookie to access it from the wordpress
      setCookie('last-searched-market', window.location.pathname);
      setCookie('last-search-market-name', search.data.name);
    }
  }, [search]);

  useEffect(() => {
    const fetchParentMarketDeatils = async () => {
      try {
        const URL = 'https://789ao60a9c.execute-api.us-east-2.amazonaws.com/prod/getMarketInfo';
        setParentMarketDetails(null);

        const data = await callApi(
          URL,
          {
            queryParams: {
              district: '',
              city: '',
              country: '',
              countryCode: '',
              marketID: parentMarketID,
            },
          },
          true,
          false,
          '',
          'GET',
          true
        );

        if (data && data.message && !data.message.error) {
          const parentMarketDetails = data.message;

          setParentMarketDetails({
            name: parentMarketDetails.name,
            city: parentMarketDetails.city,
            country: parentMarketDetails.country,
            displayName: titleCase(parentMarketDetails.displayName),
            id: parentMarketDetails.id,
            polygon: parentMarketDetails.polygon,
            multiPolygon: parentMarketDetails.multiPolygon,
            boundary: parentMarketDetails.boundary,
          });
        }
      } catch (err) {
        console.error('Error while fetchParentMarketDeatils', err);
      }
    };

    if (search.loading) {
      setParentMarketDetails(null);
    } else if (search.data.parentMarketID) {
      fetchParentMarketDeatils();
    }
  }, [search]);

  useEffect(() => {
    if (
      submarkets != 'loading' &&
      submarkets &&
      submarkets.length > 0 &&
      marketLevelActiveListings
    ) {
      const totalSubmarketsSupply = submarkets.reduce(
        (acc, submarket) => acc + submarket.supply,
        0
      );

      if (totalSubmarketsSupply / marketLevelActiveListings > 0.6) {
        setShouldShowVerifiedSubmarkets(true);
      }
    }
  }, [submarkets, marketLevelActiveListings]);

  const toggleDrawer = () => {
    setFilterOpen(!filterOpen);
  };

  const accomodationRevenueInTooltip: any =
    marketLevelDailyRate * (marketLevelOccupancyRate / 100) * 365;
  const cleaningFeeRevenueInTooltip: any = marketLevelBookingCount * marketLevelCleaningFee;

  if ((window as any).Tawk_API) {
    (window as any).Tawk_API.onLoad = () => {
      (window as any).Tawk_API.hideWidget();
    };
  }

  if (listingsExportPaymentFailed && listingsExportPaymentFailed == 'true') {
    window.alert('Payment failed! Please try again.');
    removeQueryParamsFromURL();
  }

  const fulfillListingsExportCheckout = async () => {
    const url =
      'https://010jdq035f.execute-api.us-east-2.amazonaws.com/default/fulfillStripeCheckout';

    const config = {
      body: {
        jwt: localStorage.getItem('auth-token'),
        session_id: listingsExportSessionID,
        product: 'listings_export',
        metadata: JSON.parse(localStorage.getItem('listingsExportConfig') as string),
        // isTest: true,
      },
    };

    const res = await callApi(url, config, true, false, '', 'POST');
    if (res.message.status) {
      setListingsExportCheckoutStatus(res.message.status);
    } else {
      setListingsExportCheckoutStatus('failed');
    }
  };

  const fetchSubMarketData = async (
    sortBy: string | undefined = undefined,
    bedroomType: string | undefined = undefined
  ) => {
    setSubmarkets('loading');
    setSubmarketSummary(null);
    setSubmarketParentMarketPerformance(null);

    try {
      const URL = 'https://pdsjwqykq8.execute-api.us-east-2.amazonaws.com/prod/getBestSubmarkets';

      let requestBody: any = {
        queryParams: {
          market_id: search.data.parentMarketID ? search.data.parentMarketID : search.data.id,
          bedroom: bedroomType ? bedroomType : submarketBedroomType || 'all',
          "apiType": "v2"
        },
      };

      if (sortBy || submarketSortKey !== '') {
        requestBody.queryParams.sort_by = sortBy ? sortBy : submarketSortKey;
      }

      const subMarketsData = await callApi(URL, requestBody, true, false, '', 'GET', true, undefined, true, true);

      if (subMarketsData && subMarketsData.status == 429) {
        setIsSubmarketRateLimited(true);
        setSubmarkets([]);
        setSubmarketParentMarketPerformance(null);
        setSubmarketSummary(null);
        return;
      }

      const parsedSubMarketsData = subMarketsData.message.submarkets;
      const sortedBy = subMarketsData.message.sorted_by;
      setIsSubmarketAccessAllowed(subMarketsData.message.is_user_authorized);
      sortedBy !== submarketSortKey && setSubmarketSortKey(sortedBy);
      setSubmarkets(parsedSubMarketsData);
      setSubmarketSummary(subMarketsData.message.summary);
      setSubmarketParentMarketPerformance(subMarketsData.message.parent_market_performance);
    } catch (err) {
      console.log('Error from submarket api: ', err);
      setSubmarkets(null);
      setSubmarketParentMarketPerformance(null);
    }
  };

  // useEffect(() => {
  //   setFilterApplied(isDefaultFilter());
  // }, []);

  useEffect(() => {
    if (dataStoreTimePeriodQuery && dataStoreTimeGranularityQuery) {
      setShowListingsExportPage(true);
    }
  }, []);

  useEffect(() => {
    if (listingsExportSessionID) {
      console.log('Listings export session ID: ' + listingsExportSessionID);
      setShowListingsExportPaymentFulfillmentCheckModal(true);
      fulfillListingsExportCheckout();
      removeQueryParamsFromURL();
    }
  }, []);

  useEffect(() => {
    setFilteredDiscoveryMarketsSet(
      new Set(
        discoverTabData.map(
          market => (market.district + market.city + market.countryCode) as string
        )
      )
    );
  }, [discoverTabData]);

  useEffect(() => {
    userCountryCode &&
      selectedDiscoveryCountry == '' &&
      (Object.keys(AVAILABLE_COUNTRY).includes(userCountryCode)
        ? setSelectedDiscoveryCountry(userCountryCode)
        : setSelectedDiscoveryCountry('CA'));
  }, [userCountryCode]);

  useEffect(() => {
    let freeMarketKey: string[] = [];
    let visibleMarketData = discoveryMarketType == 'top' ? freeTopMarketData : freeAllMarketData;
    for (let data of visibleMarketData) {
      let key = `${data.district}-${data.city}-${data.country_code}`;
      freeMarketKey.push(key);
    }
    setFreeMarketDataKey(freeMarketKey);
  }, [freeTopMarketData, freeAllMarketData, discoveryMarketType]);

  useEffect(() => {
    if (topRawMarketData.length > 0 && allRawMarketData.length > 0) {
      let processedData;
      const rawData = discoveryMarketType === 'top' ? topRawMarketData : allRawMarketData;
      if (isMarketDataAccessible) {
        processedData = processAndSortMarketLevelData(rawData);
      } else {
        processedData = processAndSortMarketLevelData(rawData, true);
      }

      // Processing All market Data for compare tab
      let allMarketProcessedData = [];
      if (isMarketDataAccessible) {
        allMarketProcessedData = processAndSortMarketLevelData(allRawMarketData);
        setAllMarketProcessedData(allMarketProcessedData);
      } else {
        allMarketProcessedData = processAndSortMarketLevelData(allRawMarketData, true);
        setAllMarketProcessedData(allMarketProcessedData);
      }

      setProcessedMarketData(processedData);
      setDiscoveryMarkets(processedData);
      const [min, max] = getMinmaxROI(rawData);
      setMinROI(min);
      setmaxROI(max);
    }
  }, [topRawMarketData, allRawMarketData, discoveryMarketType]);

  useEffect(() => {
    if (!cachedMarketData.loading && cachedMarketData.data) {
      setCachedHistoricalData(cachedMarketData.data?.historical_monthly_performance);
      cachedMarketData.data?.ltm_performance &&
        setCachedLTMData(cachedMarketData.data?.ltm_performance);
      cachedMarketData.data?.ltm_performance && setIsMarketCachedDataLoaded(true);
      cachedMarketData.data?.ltm_monthly_performance_breakdown &&
        setCachedMonthlyLTMData(cachedMarketData.data.ltm_monthly_performance_breakdown);

      cachedMarketData.data?.adr_breakdown?.bedroom &&
        setCachedMarketBedroomADR(cachedMarketData.data?.adr_breakdown?.bedroom);
    }
  }, [cachedMarketData]);

  useEffect(() => {
    dashboardLayoutView !== 'dashboard' &&
      setMapWidth(getSelectedViewWidth(dashboardLayoutView, matches));
  }, [matches]);

  useEffect(() => {
    if (dashboardLayoutView === 'dashboard') {
      setMapWidth(0);
      showViewingLisingFromPaginationAlert === 'showing' &&
        setShowViewingLisingFromPaginationAlert('shown');
    } else if (dashboardLayoutView === 'map') {
      setMapWidth(getSelectedViewWidth('map', matches));

      const mapViewTop = document.getElementById('overviewRight');
      mapViewTop && mapViewTop.scrollTo(0, 0);
    } else if (dashboardLayoutView === 'submarkets') {
      setMapWidth(getSelectedViewWidth('submarkets', matches));
    } else if (dashboardLayoutView === 'compare') {
      setMapWidth(0);
    }
  }, [dashboardLayoutView, showDiscoveryAI]);

  const [blockListingExport, setBlockListingExport] = useState<boolean>(false);
  const [shouldShowMainKPILoading, setShouldShowMainKPILoading] = useState<boolean>(false);

  const [showFilterComingSoonAlert, setShowFilterComingSoonAlert] = useState<boolean | 'open'>(
    false
  );

  const [showStickySubSectionNavbar, setShowStickySubSectionNavbar] = useState<boolean>(false);
  const [selectedMainKpi, setSelectedMainKpi] = useState<string>('revenue');

  const [marketGradeInfo, setMarketGradeInfo] = useState<{
    grade: string;
    summary: string[];
  } | null>(null);
  const [revenueFormattedChartData, setRevenueFormattedChartData] = useState<any>(null);

  useEffect(() => {
    setMarketGradeInfo(null);
  }, [search.data.id]);

  const [selectedGraphExportDetails, setSelectedGraphExportDetails] = useState<any>(null);

  const topSubMarketChartRef = useRef<any>(null);
  const demandByAmenitiesChartRef = useRef<any>(null);
  const mainKpiRevenueChartRef = useRef<any>(null);
  const bestPropertyTypeGraphRef = useRef<any>(null);
  const listingByPropertySizeGraphRef = useRef<any>(null);
  const listingByPropertyTypeGraphRef = useRef<any>(null);
  const cancellationPolicyGraphRef = useRef<any>(null);
  const listingsByDaysAvailableGraphRef = useRef<any>(null);
  const listingsByCapacityGraphRef = useRef<any>(null);
  const domesticVsInternationalGraphRef = useRef<any>(null);
  const monthlyAlosDataGraphRef = useRef<any>(null);
  const monthlyBltDataGraphRef = useRef<any>(null);
  const dailyOccupancyRateCalendarHistoricalGraphRef = useRef<any>(null);
  const dailyOccupancyRateCalendarFutureGraphRef = useRef<any>(null);
  const configurableFilterBarCardSelectionModalRef = useRef<any>({
    tabSelected: 'Revenue',
    filterType: 'Bedrooms',
  });

  useEffect(() => {
    if (user && user.blockListingExport) {
      setBlockListingExport(true);
    }
    setShowDiscoveryAI(user.showDiscoveryAI);
  }, [user]);

  useEffect(() => {
    if (isLoggedIn && !isUserAllowed) {
      setTimeout(() => {
        if (user.credits > 0) {
          setShowSearchCreditDeductWarningModal(true);
        }
      }, 5020);
    }
  }, [isUserAllowed, isLoggedIn]);

  useEffect(() => {
    if (isDemoMarket) {
      setShouldShowMainKPILoading(!cachedLTMData ? true : false);
      return;
    }

    if (isDisabled) {
      setShouldShowMainKPILoading(false);
      return;
    }

    if (!cachedLTMData) {
      setShouldShowMainKPILoading(true);
    } else {
      setShouldShowMainKPILoading(false);
    }
  }, [cachedLTMData, isDisabled, isDemoMarket]);

  const isPDFDataToDownloadLoading = cachedMarketData.loading || topHosts.loading;

  const setHistoricalGraphData = (
    historicalMarketPerformanceData,
    type: 'full' | 'sample' = 'sample'
  ) => {
    const [dailyRateHistoricalData, dailyYoYChange] = getHistoricalData(
      historicalMarketPerformanceData,
      'daily'
    );

    setDailyRateHistorical(dailyRateHistoricalData);
    setDailyRatePast6MonthsHistorical(dailyRateHistoricalData.slice(-6));
    setDailyYoYChange(dailyYoYChange);

    if (type === 'full') {
      const [occupancyRateHistoricalData, occupancyYoYChange] = getHistoricalData(
        historicalMarketPerformanceData,
        'occupancy'
      );

      const [revenueHistoricalData, revenueYoYChange] = getHistoricalData(
        historicalMarketPerformanceData,
        'revenue'
      );

      const [listingHistoricalData, listingYoYChange] = getHistoricalData(
        historicalMarketPerformanceData,
        'listing'
      );

      setOccupancyRateHistoricalData(occupancyRateHistoricalData);
      setRevenueHistorical(revenueHistoricalData);
      setListingHistorical(listingHistoricalData);
      setOccupancyPast6MonthsHistorical(occupancyRateHistoricalData.slice(-6));
      setRevenuePast6MonthsHistorical(revenueHistoricalData.slice(-6));
      setListingPast6MonthsHistorical(listingHistoricalData.slice(-6));
      setOccupancyYoYChange(occupancyYoYChange);
      setRevenueYoYChange(revenueYoYChange);
      setListingYoYChange(listingYoYChange);
    }
  };

  useEffect(() => {
    if (dashboardLayoutView !== 'map' && cachedLTMData) {
      const marketLevelOccupancyRate = getMarketKpiSelectedPercentileWise(
        cachedLTMData,
        'occupancy_rate',
        selectedPercentileInMainKPICard
      );

      const marketLevelRevenue = getMarketKpiSelectedPercentileWise(
        cachedLTMData,
        'revenue',
        selectedPercentileInMainKPICard
      );

      const marketLevelCleaningFee = getMarketKpiSelectedPercentileWise(
        cachedLTMData,
        'cleaning_fee',
        selectedPercentileInMainKPICard
      );

      const marketLevelBookingCount = cachedLTMData['booking'];

      const marketLevelDailyRate = getMarketKpiSelectedPercentileWise(
        cachedLTMData,
        'adr',
        selectedPercentileInMainKPICard
      );

      const marketLevelAvailiableListing = cachedLTMData['supply'];

      setMarketLevelOccupancyRate(marketLevelOccupancyRate);
      setMarketLevelRevenuePotential(marketLevelRevenue);
      setMarketLevelDailyRate(marketLevelDailyRate);
      setMarketLevelActiveListings(marketLevelAvailiableListing);
      setMarketLevelCleaningFee(marketLevelCleaningFee);
      setMarketLevelBookingCount(marketLevelBookingCount);
    }

    if (cachedHistoricalData && cachedMarketDataWithoutFilters) {
      setHistoricalGraphData(
        filterApplied && selectedMainKpi == 'listing'
          ? cachedMarketDataWithoutFilters?.historical_monthly_performance
          : cachedHistoricalData,
        cachedMarketData.data?.is_user_allowed ? 'full' : 'sample'
      );
    }
  }, [
    cachedHistoricalData,
    cachedMarketData,
    selectedPercentileInMainKPICard,
    dashboardLayoutView,
    selectedMainKpi,
    cachedMarketDataWithoutFilters,
  ]);

  const handleSubKPITabClick = (tabId: string) => {
    const element = document.getElementById(tabId);
    if (element) {
      // Create a new element above the selected element
      const newElement = document.createElement('div');
      newElement.style.height = '1px';
      newElement.style.position = 'relative';
      newElement.style.top = dashboardLayoutView === 'map' ? '-165px' : '-200px';

      // Insert the new element before the selected element
      element.parentNode && element.parentNode.insertBefore(newElement, element);

      // Scroll to the new element
      newElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      setTimeout(() => {
        newElement && newElement.remove();
      }, 300);
    }
  };

  const handleOnClickTabLoading = (isMapWidthChanging: boolean = true) => {
    setIsTabChanging(isMapWidthChanging ? true : false);
  };

  const handlePageUrlWithTabSwitching = (tab: string = '') => {
    let targetUrl = '';

    if (tab === 'compare') {
      targetUrl = '/markets/compare';
    } else {
      targetUrl = currentMarketUrl.current;
    }
    if (window.location.pathname !== targetUrl) {
      window.history.pushState({}, 'overview', targetUrl);
    }
  };

  const overviewDashboardTabs = [
    {
      label: 'Market Summary',
      mobileLabel: 'Summary',
      value: 'dashboard',
      icon: <DashboardIcon />,
      onclick: () => {
        setMapWidth(getSelectedViewWidth('dashboard', matches));
        setDashboardLayoutView('dashboard');
      },
    },
    {
      label: `${
        !marketTotalListingsCount.loading && marketTotalListingsCount.data
          ? abbreviateNumber(marketTotalListingsCount.data, false)
          : ''
      } Listings (Map)`,
      mobileLabel: 'Listings (Map)',
      value: 'map',
      icon: <MapIcon />,
      onclick: () => {
        setMapWidth(MAP_SIZE_MAP_VIEW_COLLAPSED_DESKTOP);
        setDashboardLayoutView('map');
        logAmplitudeEvent('map_viewed_in_overview', { cta: 'navbar' });
      },
    },

    {
      label:
        submarkets === 'loading' ? (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Skeleton variant="text" sx={{ fontSize: '20px', width: '20px', mr: '5px' }} />
            <Skeleton variant="text" sx={{ fontSize: '20px', width: '50px' }} />
          </Box>
        ) : submarkets && submarkets.length > 0 ? (
          `${submarkets.length} Submarkets`
        ) : (
          `Submarkets`
        ),
      mobileLabel: 'Submarkets',
      value: 'submarkets',
      icon: submarkets === 'loading' ? <></> : <SubjectIcon />,
      onclick: () => {
        if (submarkets === 'loading') return;

        setMapWidth(getSelectedViewWidth('submarkets', matches));
        setDashboardLayoutView('submarkets');
      },
      beta: true,
    },
  ].filter(tab => {
    if (!submarkets && tab.value === 'submarkets') {
      return false;
    }
    return true;
  });

  const subKpiTabsMetadata = [
    {
      label: 'Market Overview',
      value: 'overview',
      onclick: () => {
        setSubKpiSelected('overview');
        handleSubKPITabClick('overview-tab');
      },
    },
    {
      label: 'Best Areas',
      value: 'location',
      onclick: () => {
        setSubKpiSelected('location');
        handleSubKPITabClick('location-tab');
      },
    },
    {
      label: 'Best Property Type',
      value: 'listings',
      onclick: () => {
        setSubKpiSelected('listings');
        handleSubKPITabClick('listings-tab');
      },
    },
    {
      label: 'Guest Profile',
      value: 'guests',
      onclick: () => {
        setSubKpiSelected('guests');
        handleSubKPITabClick('guests-tab');
      },
    },
    {
      label: 'Seasonality',
      value: 'seasonality',
      onclick: () => {
        setSubKpiSelected('seasonality');
        handleSubKPITabClick('seasonality-tab');
      },
    },
  ];

  const ComponentFilterSupportComingSoonText = (_customText: string | undefined = undefined) => {
    return <></>;
  };

  const isInViewport = (element: HTMLElement | null, container: HTMLElement | null) => {
    if (!element || !container) return false;
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    // Check if the element's top is within the container's visible area
    return elementRect.top >= containerRect.top && elementRect.top < containerRect.bottom;
  };

  const handleScroll = _debounce((event: Event) => {
    const target = event.target as HTMLElement;
    const scrollPosition: number = target.scrollTop;
    setDashboardScrollPositionTop(scrollPosition);
    setShowStickySubSectionNavbar(
      scrollPosition > OVERVIEW_PAGE_STICKY_SUBSECTION_NAVBAR_THRESHOLD
    );

    if (dashboardLayoutView === 'dashboard') {
      const container = document.getElementById('overviewRight');
      subKpiTabsMetadata.forEach(subKpiSection => {
        const tabElement = document.getElementById(`${subKpiSection.value}-tab`);
        if (isInViewport(tabElement, container)) {
          setSubKpiSelected(subKpiSection.value);
        }
      });
    }

    // Check if user has scrolled to the end
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    if (scrollPosition + clientHeight >= scrollHeight - 1) {
      // User has scrolled to the end
      logAmplitudeEvent('overview_scrolled_to_end', {
        market_id: search.data.id,
        is_submarket: search.data.isSubmarket,
      });
    }
  }, MAP_SIZE_MAP_VIEW_COLLAPSED_DESKTOP);

  const handleResizeEndDashboardView = () => {
    setIsTabChanging(false);
  };

  useEffect(() => {
    if (!['dashboard', 'map'].includes(dashboardLayoutView)) return;
    const rightViewElement = document.getElementById('overviewRight');

    rightViewElement && rightViewElement.addEventListener('scroll', handleScroll);

    // Observes the resize handling for Map Container
    const leftMapView = document.getElementById('overviewLeft');
    leftMapView && leftMapView.addEventListener('transitionend', handleResizeEndDashboardView);

    return () => {
      rightViewElement && rightViewElement.removeEventListener('scroll', handleScroll);
      leftMapView && leftMapView.removeEventListener('transitionend', handleResizeEndDashboardView);
    };
  }, [search, dashboardLayoutView]);

  const callDynamicMarkers = async (
    marketId: number | string,
    boundsObj: Bounds,
    filterVal: FilterVal,
    page = 1,
    returnSupply = false
  ) => {
    setMapLoading(true);
    const asyncVisibleListings = await getDynamicMarkers(
      marketId,
      search.data.polygon || search.data.multiPolygon,
      boundsObj,
      isUserAllowed,
      filterCacheLoading || search.data.isSubmarket || showFilterComingSoonAlert || !isUserAllowed
        ? null
        : filterVal,
      page,
      returnSupply,
      setFilterCacheLoading,
      listingSortingKey
    );
    setVisibleMapListings(asyncVisibleListings);

    setMapLoading(false);
  };

  const callBookingMarkers = async (marketId: number | string, boundsObj: Bounds) => {
    const asyncVisibleListings = await getBookingMarkers(
      marketId,
      search.data.polygon || search.data.multiPolygon,
      boundsObj
    );
    setVisibleBookingListings(asyncVisibleListings);
  };

  const resetFilter = (filterNamesToReset: string[] = []) => {
    const isFullReset = filterNamesToReset.length === 0;

    setFilterVal(prev => {
      const newFilterVal = { ...prev };

      if (isFullReset || filterNamesToReset.includes('roomType'))
        newFilterVal.roomType = [...DEFAULT_FILTER_VALUES.roomType];
      if (isFullReset || filterNamesToReset.includes('bedrooms'))
        newFilterVal.bedrooms = [...DEFAULT_FILTER_VALUES.bedrooms];
      if (isFullReset || filterNamesToReset.includes('bathrooms'))
        newFilterVal.bathrooms = [...DEFAULT_FILTER_VALUES.bathrooms];
      if (isFullReset || filterNamesToReset.includes('revenueFilter'))
        newFilterVal.revenueFilter = [...DEFAULT_FILTER_VALUES.revenueFilter];
      if (isFullReset || filterNamesToReset.includes('priceTierFilter'))
        newFilterVal.priceTierFilter = [...DEFAULT_FILTER_VALUES.priceTierFilter];
      if (isFullReset || filterNamesToReset.includes('accommodates'))
        newFilterVal.accommodates = [...DEFAULT_FILTER_VALUES.accommodates];
      if (isFullReset || filterNamesToReset.includes('minimum_nights'))
        newFilterVal.minimum_nights = DEFAULT_FILTER_VALUES.minimum_nights;
      if (isFullReset || filterNamesToReset.includes('number_of_reviews'))
        newFilterVal.number_of_reviews = DEFAULT_FILTER_VALUES.number_of_reviews;
      if (isFullReset || filterNamesToReset.includes('rating'))
        newFilterVal.rating = [...DEFAULT_FILTER_VALUES.rating];
      if (isFullReset || filterNamesToReset.includes('hostType'))
        newFilterVal.hostType = DEFAULT_FILTER_VALUES.hostType as 'both';

      if (isFullReset || filterNamesToReset.includes('amenities')) {
        newFilterVal.amenities = { ...DEFAULT_FILTER_VALUES.amenities };
      }

      return newFilterVal;
    });

    const localStorageKeys = [
      { key: 'roomType', id: 'room_type' },
      { key: 'bedrooms', id: 'bedroom_number' },
      { key: 'bathrooms', id: 'bathroom_number' },
      { key: 'accommodates', id: 'no_of_people' },
      { key: 'hostType', id: 'host_type' },
      { key: 'amenities', id: 'amenities' },
      { key: 'minimum_nights', id: 'minimum_nights' },
      { key: 'number_of_reviews', id: 'number_of_reviews' },
      { key: 'rating', id: 'rating' },
    ];

    localStorageKeys.forEach(({ key, id }) => {
      if (isFullReset || filterNamesToReset.includes(key)) {
        localStorage.removeItem(id);
      }
    });
  };

  const dashboardLoadingScreenFullSize = (
    <Grid container sx={{ padding: '20px' }}>
      <Grid item xs={12}>
        {/* Use the loading animation twice to fill the page */}
        {[1, 2].map((_, index) => (
          <Fragment key={`skeleton-${index}`}>
            <Skeleton height={120} sx={{ borderRadius: 4 }} />
            <Skeleton height={60} animation="wave" sx={{ borderRadius: 4 }} />
            <Skeleton height={40} sx={{ borderRadius: 4 }} />
            <Skeleton height={50} animation="wave" sx={{ borderRadius: 4 }} />
          </Fragment>
        ))}
      </Grid>
    </Grid>
  );

  const [seasonalityGraphData, setSeasonalityGraphData] = useState<BarGraphData[]>([]);
  const SeasonalityChartMemoed = useMemo(
    () =>
      !cachedLTMData ? (
        <LoadingCard height={150} type="bars" />
      ) : (
        <>
          <SeasonalityChart
            metricsHistoricalData={revenueHistoricalData}
            metricKey={'revenue'}
            selectedPercentileInMainKPICard={selectedPercentileInMainKPICard}
            currency={currency}
            setSeasonalityGraphData={setSeasonalityGraphData}
          />
        </>
      ),
    [revenueHistoricalData, currency, cachedMarketData]
  );

  const { yearlyYoYChange, nextYearPredcition } = calculateHistoricalTrends(
    revenueHistoricalData,
    'revenue',
    selectedPercentileInMainKPICard
  );

  const [trendGraphData, setTrendGraphData] = useState<BarGraphData[]>([]);
  const TrendsChartMemo = useMemo(() => {
    return cachedMarketData.loading ? (
      <LoadingCard height={150} type="bars" />
    ) : (
      <>
        <TrendsChart
          yearlyYoYChange={yearlyYoYChange}
          isDisabled={isDisabled}
          currency={currency}
          setTrendGraphData={setTrendGraphData}
        />
      </>
    );
  }, [revenueHistoricalData, currency, cachedMarketData]);

  const MainKPICardMemoed = (
    <>
      {isCachedMarketDataSample && (
        <Alert severity="info">
          The current Market Overview graph shows data from up to 100 listings. We're working on
          updating it with complete data, which should be ready in 15 minutes. If you don't see the
          update after 15 minutes, please contact support@airbtics.com.
        </Alert>
      )}

      <Box
        sx={{
          width: '100%',
          // height: matches ? '500px' : '460px',
          boxShadow: '0 4px 10px #0003',
          borderRadius: '8px',
        }}
      >
        <MainKPICard
          constantDummyData={
            parseMessageHistoricalData(deepCopyObject(dummyMarketHistoricalData))
              .historical_monthly_performance
          }
          currency={currency}
          marketLevelOccupancyRate={marketLevelOccupancyRate}
          occupancyYoYChange={occupancyYoYChange}
          marketLevelRevenuePotential={marketLevelRevenuePotential}
          marketLevelDailyRate={marketLevelDailyRate}
          revenueYoYChange={revenueYoYChange}
          dailyYoYChange={dailyYoYChange}
          marketLevelActiveListings={marketLevelActiveListings}
          marketLevelCleaningFee={marketLevelCleaningFee}
          marketLevelBookingCount={marketLevelBookingCount}
          listingYoYChange={listingYoYChange}
          occupancyRateHistoricalData={occupancyRateHistoricalData}
          occupancyPast6MonthsHistoricalData={occupancyPast6MonthsHistoricalData}
          revenueHistoricalData={revenueHistoricalData}
          revenuePast6MonthsHistoricalData={revenuePast6MonthsHistoricalData}
          dailyRateHistoricalData={dailyRateHistoricalData}
          dailyRatePast6MonthsHistoricalData={dailyRatePast6MonthsHistoricalData}
          listingHistoricalData={listingHistoricalData}
          listingPast6MonthsHistoricalData={listingPast6MonthsHistoricalData}
          matches={matches}
          handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'kpiGraph')}
          search={search}
          selectedPercentile={selectedPercentileInMainKPICard}
          setSelectedpercentile={setSelectedPercentileInMainKPICard}
          SeasonalityChartMemoed={SeasonalityChartMemoed}
          TrendsChartMemo={TrendsChartMemo}
          lastYearMarketTrend={nextYearPredcition}
          isMarketCacheLoading={cachedMarketData.loading}
          ComponentFilterSupportComingSoonText={ComponentFilterSupportComingSoonText()}
          filterCacheLoading={filterCacheLoading}
          selectedMainKpi={selectedMainKpi}
          setSelectedMainKpi={setSelectedMainKpi}
          setRevenueFormattedChartData={setRevenueFormattedChartData}
          mainKpiRevenueChartRef={mainKpiRevenueChartRef}
          seasonalityGraphData={seasonalityGraphData}
          trendGraphData={trendGraphData}
          setSelectedGraphExportDetails={setSelectedGraphExportDetails}
        />
      </Box>
    </>
  );

  const CapacityFilterCardSingleBar = (
    <SingleBarFilterCard
      title="Capacity"
      catagoryMap={'dynamicRange'}
      tooltipTitleSuffix="Guests"
      isLoading={cachedMarketData.loading || !cachedMarketData.data?.supply_breakdown?.accommodates}
      data={isMarketCachedDataLoaded ? cachedMarketData.data?.supply_breakdown?.accommodates : null}
      metricTabs={['supply']}
      color={COLOR_PRIMARY}
      currency={currency}
      handleOnSampleClick={handleOnSampleClick}
      tooltipText={'Capacity is the number of guests a property can accept.'}
      isGraphSizeSmall={true}
      maxBarSize={13}
      isGraphLocked={false}
      isDisabled={isDisabled}
      totalListingsCount={marketLevelActiveListings}
      parent="listing_by_capacity"
      graphRef={listingsByCapacityGraphRef}
    />
  );

  const ActiveDaysFilterCardSingleBar = (
    <SingleBarFilterCard
      title="Days Available"
      catagoryMap={{
        '90-180': { name: '90-180' },
        '181-270': { name: '181-270' },
        '271-367': { name: '271-365' },
      }}
      tooltipTitleSuffix="days"
      isLoading={
        cachedMarketData.loading || !cachedMarketData.data?.supply_breakdown?.active_days_count
      }
      data={
        isMarketCachedDataLoaded ? cachedMarketData.data?.supply_breakdown?.active_days_count : null
      }
      metricTabs={['supply']}
      color={COLOR_PRIMARY_1_700}
      currency={currency}
      handleOnSampleClick={handleOnSampleClick}
      maxBarSize={13}
      isGraphSizeSmall={true}
      xAxisTitle={'days'}
      tooltipText={
        'Days available is the number of days an Airbnb listing was available for booking in the last 12 months.'
      }
      isGraphLocked={false}
      isDisabled={isDisabled}
      totalListingsCount={marketLevelActiveListings}
      parent="listing_by_days_available"
      graphRef={listingsByDaysAvailableGraphRef}
    />
  );

  const PropertySizeCardMemoed = (
    <PropertySizeCard
      cachedData={
        isMarketCachedDataLoaded ? cachedMarketData.data?.supply_breakdown?.bedroom : null
      }
      viewMode={dashboardLayoutView}
      isDynamicLoading={dynamicLoadingMapViewKPI}
      multipleMarkersCallLoading={cachedMarketData.loading}
      noDataInMarket={noDataInMarket}
      totalListingsCount={marketLevelActiveListings}
      listingByPropertySizeGraphRef={listingByPropertySizeGraphRef}
    />
  );

  const PropertyTypeChannelCancelationPolicyPieChat = (
    <PropertyTypeChannelPieChart
      cachedData={
        isMarketCachedDataLoaded
          ? {
              property_type: cachedMarketData.data?.supply_breakdown?.property_type,
              cancellation_policy: cachedMarketData.data?.supply_breakdown?.cancellation_policy,
            }
          : null
      }
      viewMode={dashboardLayoutView}
      isDynamicLoading={dynamicLoadingMapViewKPI}
      multipleMarkersCallLoading={cachedMarketData.loading}
      noDataInMarket={noDataInMarket}
      totalListingsCount={marketLevelActiveListings}
      listingByPropertyTypeGraphRef={listingByPropertyTypeGraphRef}
      cancellationPolicyGraphRef={cancellationPolicyGraphRef}
    />
  );

  const AmenityCardMemoed = (
    <AmenityCard
      cachedData={
        isMarketCachedDataLoaded ? cachedMarketData.data?.supply_breakdown?.amenity : null
      }
      viewMode={dashboardLayoutView}
      isDynamicLoading={dynamicLoadingMapViewKPI}
      multipleMarkersCallLoading={cachedMarketData.loading}
      noDataInMarket={noDataInMarket}
      totalListingsCount={marketLevelActiveListings}
    />
  );

  const TopCitiesCardMemoed = cachedMarketData.loading ? (
    <LoadingCard height={150} type="bars" />
  ) : (
    <TopCitiesCard
      graph={cachedMarketData}
      isDisabled={isDisabled}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'guestOriginGraphs')}
      matches={matches}
      userTailorMarketAccess={userTailorMarketAccess}
      warningTextComponent={ComponentFilterSupportComingSoonText()}
      setSelectedGraphExportDetails={setSelectedGraphExportDetails}
      noDataInMarket={noDataInMarket}
    />
  );

  const TopCountriesCardMemoed = cachedMarketData.loading ? (
    <LoadingCard height={150} type="bars" />
  ) : (
    <TopCountriesCard
      graph={cachedMarketData}
      isDisabled={isDisabled}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'guestOriginGraphs')}
      matches={matches}
      userTailorMarketAccess={userTailorMarketAccess}
      tooltipSuffix={' of total guests'}
      warningTextComponent={ComponentFilterSupportComingSoonText()}
      setSelectedGraphExportDetails={setSelectedGraphExportDetails}
    />
  );

  const GuestLanguageCardMemoed = cachedMarketData.loading ? (
    <LoadingCard height={150} type="bars" />
  ) : (
    <GuestLanguageCard
      graph={cachedMarketData}
      isDisabled={isDisabled}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'guestOriginGraphs')}
      matches={matches}
      userTailorMarketAccess={userTailorMarketAccess}
      tooltipSuffix={' of total guests'}
      warningTextComponent={ComponentFilterSupportComingSoonText()}
      setSelectedGraphExportDetails={setSelectedGraphExportDetails}
    />
  );

  const InternationDomesticGuestsCardMemoed = cachedMarketData.loading ? (
    <LoadingCard height={350} type="bars" />
  ) : (
    <InternationDomesticGuestsCard
      graph={cachedMarketData}
      isDisabled={isDisabled}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'guestOriginGraphs')}
      userTailorMarketAccess={userTailorMarketAccess}
      warningTextComponent={ComponentFilterSupportComingSoonText()}
      domesticVsInternationalGraphRef={domesticVsInternationalGraphRef}
      setSelectedGraphExportDetails={setSelectedGraphExportDetails}
    />
  );

  const HostsMemoed = (
    <Hosts
      topHosts={topHosts}
      currency={currency}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'hostsTable')}
      user={user}
      matches={matches}
      dashboardLayoutView={dashboardLayoutView}
      userTailorMarketAccess={userTailorMarketAccess}
      searchObj={search.data}
      isUserAllowed={isUserAllowed}
      cachedMarketData={cachedMarketData}
      warningTextComponent={ComponentFilterSupportComingSoonText()}
    />
  );

  const StatisticBox = ({ label, value, change, conversionFunction, hideKPI, tooltipconfig }) => {
    const upgradeMessage = useUpgradeMesageExpertFeature();
    return (
      <Box style={{ flexBasis: '24%', height: 'auto', overflow: 'hidden' }}>
        <Box
          sx={{
            height: '50px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            color: '#000',
            padding: '3px 10px',
          }}
        >
          <Typography
            sx={{
              fontWeight: '500',
              fontSize: '14px',
              maxWidth: '70%',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {label}
          </Typography>
          <InfoButton
            tooltipContent={<MainKPITooltip tooltipConfig={tooltipconfig} matches={matches} />}
          />
        </Box>
        <Box
          sx={{
            height: '40%',
            width: '100%',
            color: '#000',
            padding: '0px 10px',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: '1000',
              color: KPI_CARD_COLOR,
            }}
          >
            {hideKPI ? (
              <>
                <LockSharpIcon
                  onClick={() => handleOnSampleClick('expert')}
                  style={{ color: COLOR_PRIMARY, fontSize: '30px', cursor: 'pointer' }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: COLOR_PRIMARY_2,
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    textDecoration: 'underline',
                  }}
                  onClick={() => handleOnSampleClick('expert')}
                >
                  {upgradeMessage}
                </Typography>
              </>
            ) : (
              <span>{value ? conversionFunction(value) : 'No Data'}</span>
            )}
          </Typography>
        </Box>
      </Box>
    );
  };

  const revenueTooltipConfig = kpiTooltipConfig['revenue'];

  const MainKpiMapView = (isMapCollapsed: boolean) => {
    return dynamicLoadingMapViewKPI || mapOverviewKpi.loading ? (
      <Grid container item>
        <LoadingCard height={60} type="bars" barCount={1} />
      </Grid>
    ) : (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: isMapCollapsed ? 'row' : 'column',
          flexWrap: isMapCollapsed ? 'wrap' : 'no-wrap',
          justifyContent: isMapCollapsed ? 'center' : 'flex-start',
        }}
      >
        {/* Occupancy */}
        <StatisticBox
          label={'Occupancy Rate'}
          value={mapOverviewKpi.data.occupancy_rate}
          change={occupancyYoYChange}
          conversionFunction={value => `${value} %`}
          hideKPI={!isUserAllowed}
          tooltipconfig={kpiTooltipConfig.occupancy}
        />
        {/* Revenue */}
        <StatisticBox
          label={'Revenue'}
          value={mapOverviewKpi.data.revenue}
          change={revenueYoYChange}
          conversionFunction={value =>
            `${currency && exchangeRates[currency].symbol}${formatCurrency(
              String(convertCurrency(currency, value))
            )}`
          }
          hideKPI={!isUserAllowed}
          tooltipconfig={revenueTooltipConfig}
        />
        {/* Nightly Rate */}
        <StatisticBox
          label="Nightly Rate"
          value={mapOverviewKpi.data.adr}
          change={dailyYoYChange}
          conversionFunction={value =>
            `${currency && exchangeRates[currency].symbol}${formatCurrency(
              String(convertCurrency(currency, value))
            )}`
          }
          hideKPI={!isUserAllowed}
          tooltipconfig={kpiTooltipConfig.daily}
        />
        {/* Active Listing */}
        <StatisticBox
          label="Active Listings"
          value={mapOverviewKpi.data.supply}
          change={listingYoYChange}
          conversionFunction={value => formatCurrency(value.toString())}
          hideKPI={false}
          tooltipconfig={kpiTooltipConfig.listing}
        />
      </Box>
    );
  };

  const ListingCardGrid = useMemo(() => {
    const noListingsInView = visibleMapListings.length == 0;
    if (!bounds || Object.keys(bounds).length == 0 || noListingsInView) {
      return <NoDataCard height={150} borderRadius={8} />;
    }

    if (visibleMapListings.length == 0 || mapLoading) {
      return <ListingsGridLoadingCard isMapCollapsed={isMapCollapsed} />;
    }

    setMapViewportListings({ count: visibleMapSupply.data, loading: false });

    if (!visibleMapListings || visibleMapListings.length == 0) {
      return <NoDataCard height={150} borderRadius={8} />;
    }

    return (
      <Grid
        item
        container
        spacing={1}
        justifyContent={'center'}
        xs={12}
        sx={{
          position: 'relative',
          height: '100%',
          overflow: 'auto',
          overflowX: 'clip',
          marginTop: '0px',
          paddingBottom: '10px',
          marginBottom: '10px',
        }}
      >
        {visibleMapListings
          .slice(listingsGridStartIndexOffset * 9, listingsGridStartIndexOffset * 9 + 9)
          .map(data => {
            const bedroom = data.bedrooms?.toLowerCase() == 'studio' ? 0 : data.bedrooms;
            const referenceADR = cachedMarketBedroomADR ? cachedMarketBedroomADR[bedroom] : null;
            return (
              <Grid
                key={`listing-card-${data.listingID}`}
                container
                item
                xs={12}
                md={isMapCollapsed ? 6 : 12}
                lg={isMapCollapsed ? 4 : 12}
                xl={isMapCollapsed ? 4 : 12}
                onMouseEnter={() => {
                  if (search.data.name)
                    updateDynamoDBForUserVerification(
                      search.data.name,
                      setSubscriptionModalOpen,
                      user
                    );
                  setCurrentHoveredCardFromDataGrid(data.listingID);
                }}
                onMouseLeave={() => {
                  setCurrentHoveredCardFromDataGrid(null);
                }}
                onClick={() => {
                  setCurrentHoveredCardFromDataGrid(data.listingID);
                }}
              >
                <ListingCardErrorBoundary>
                  <SimplifiedAirbnbPointerBody
                    data={data}
                    currency={currency}
                    canDelete={false}
                    isDisabled={isDisabled}
                    deleteListing={() => {}}
                    setOpenFullView={() => {}}
                    isHidden={false}
                    isEstimator={false}
                    setTopPerformingListingOnFocus={setTopPerformingListingOnFocus}
                    setTopPerformingListingModalOpen={setTopPerformingListingModalOpen}
                    handleOnSampleClick={reqPlan =>
                      handleOnSampleClick(reqPlan, 'individualListingsKPI')
                    }
                    referenceADR={referenceADR}
                  />
                </ListingCardErrorBoundary>
              </Grid>
            );
          })}
        <Grid container item xs={12} justifyContent="center" alignItems="flex-end">
          <Grid item className="notranslate">
            <Pagination
              page={listingGridPage}
              count={Math.ceil(visibleMapSupply.data / 9)}
              variant="outlined"
              onChange={async (event, value) => {
                if (!isUserAllowed && value >= 3) {
                  handleOnSampleClick();
                  return;
                }

                setListingGridPage(value);

                if (showViewingLisingFromPaginationAlert !== 'shown' && value > 2) {
                  setShowViewingLisingFromPaginationAlert('showing');
                }

                await callDynamicMarkers(search.data.id, bounds, filterVal, value);
                return;
              }}
              renderItem={item => {
                const { page, ...other } = item;
                const isLocked = page != null && page > 2 && !isUserAllowed;

                return (
                  <Tooltip title={isLocked ? 'Upgrade to view more listings' : ''}>
                    <PaginationItem
                      {...item}
                      sx={{
                        color: isLocked ? COLOR_GREY_800 : 'inherit',
                      }}
                    />
                  </Tooltip>
                );
              }}
            />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <ShowAlertTextBox
            isOpen={showViewingLisingFromPaginationAlert === 'showing'}
            type={'warning'}
            text={
              'Looking for a specific listing? Zoom in on the map, and this list will display only the listings visible in the current map view.'
            }
          />
        </Grid>
      </Grid>
    );
  }, [
    bounds,
    listingsGridStartIndexOffset,
    listingSortingKey,
    currency,
    visibleMapListings,
    mapLoading,
    visibleMapSupply,
  ]);

  const fetchListingsAfterSortingKeyUpdate = async (
    sortingKey: 'top_properties' | 'review_count' | 'revenue' | 'occupancy' | 'daily_rate'
  ) => {
    const { filters } =
      filterCacheLoading || search.data.isSubmarket || showFilterComingSoonAlert || !isUserAllowed
        ? { filters: {} }
        : parseFilterValToApiResponse(filterVal);
    setMapLoading(true);
    const asyncVisibleListings = await getDynamicMarkers(
      search.data.id,
      search.data.polygon || search.data.multiPolygon,
      bounds,
      isUserAllowed,
      filterCacheLoading || search.data.isSubmarket || showFilterComingSoonAlert || !isUserAllowed
        ? null
        : filterVal,
      1,
      false,
      null,
      sortingKey
    );
    setVisibleMapListings(asyncVisibleListings);
    setMapLoading(false);
  };

  const getSubSectionHeading = (
    title: string | JSX.Element,
    id: string,
    showDownloadButton: boolean = false,
    downloadKey?: any,
    tooltipText?: string,
    filterButton?: JSX.Element
  ) => {
    return (
      <Grid
        container
        item
        xs={12}
        id={id}
        sx={{
          marginLeft: 0,
          padding: '10px auto',
          fontSize: '20px',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h5" className="marketTitle" sx={{ fontSize: 'inherit' }}>
          <span>{title} </span>
          {tooltipText ? <InfoButton tooltipContent={tooltipText} /> : <></>}
        </Typography>
        {filterButton ? filterButton : <></>}
      </Grid>
    );
  };

  const isActiveListingCountHeaderLoading =
    visibleMapSupply.loading || visibleMapSupply.data == null;
  const activeListingCountDataToUse = visibleMapSupply.data;

  const ActiveListingCountHeader = getSubSectionHeading(
    <Box sx={{ display: 'flex' }}>
      <span style={{ color: COLOR_PRIMARY, marginRight: '5px' }}>
        {isActiveListingCountHeaderLoading ? (
          <CircularProgress size={20} />
        ) : (
          <span>{formatCurrency(activeListingCountDataToUse + '')}</span>
        )}
      </span>{' '}
      <span>listings: {search.data ? marketDisplayName : ''}</span>
      <Box sx={{ cursor: 'pointer', marginLeft: '10px' }}>
        <Typography
          variant="body1"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            color: COLOR_PRIMARY_2,
          }}
          onClick={() => {
            logAmplitudeEvent('listing_export_modal_opened');
            setShowListingsExportPage(true);
          }}
        >
          <DownloadIcon sx={{ marginRight: '2px' }} /> <span>Export</span>
        </Typography>
      </Box>
    </Box>,
    'active-listing-heading',
    false,
    null,
    '', //TODO add tooltip here
    <>
      <Box>
        <FormControl>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={listingSortingKey}
            label=""
            onChange={e => {
              logAmplitudeEvent('listing_grid_sorted');
              if (isUserAllowed || isDemoMarket) {
                setListingSortingKey(e.target.value);
                fetchListingsAfterSortingKeyUpdate(
                  e.target.value as
                    | 'top_properties'
                    | 'review_count'
                    | 'revenue'
                    | 'occupancy'
                    | 'daily_rate'
                );
              } else {
                handleOnSampleClick();
              }
            }}
            sx={{
              '& .MuiSelect-select': {
                padding: '4px 0px 4px 10px',
              },
              marginRight: '10px',
            }}
          >
            <MenuItem value={'top_properties'}>Top properties</MenuItem>
            <MenuItem value={'review_count'}>
              Review Count{' '}
              {!isUserAllowed && (
                <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
              )}
            </MenuItem>
            <MenuItem value={'revenue'}>
              Revenue{' '}
              {!isUserAllowed && (
                <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
              )}
            </MenuItem>
            <MenuItem value={'occupancy'}>
              Occupancy{' '}
              {!isUserAllowed && (
                <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
              )}
            </MenuItem>
            <MenuItem value={'daily_rate'}>
              Daily Rate{' '}
              {!isUserAllowed && (
                <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
              )}
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    </>
  );

  const ActiveListingsCountHeaderLoading = (
    <Grid
      item
      xs={12}
      sx={{
        margin: '10px 10px',
        marginLeft: 0,
        padding: '10px auto',
        fontSize: '20px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {isUserAllowed ? (
        <Typography variant="h5" className="marketTitle" sx={{ fontSize: 'inherit' }}>
          Fetching listings <span>{search.data ? 'in ' + marketDisplayName : ''}</span>... Please
          wait while we gather the latest data {<CircularProgress size={20} />}
        </Typography>
      ) : (
        <Skeleton variant="text" sx={{ width: '300px' }} height="50px" />
      )}
    </Grid>
  );

  const SaveSubMarketButton = search.data.isSubmarket ? (
    <></>
  ) : (
    <Box
      sx={{
        transform: 'scale(0.7) translateX(-30px)',
      }}
    >
      <TooltipedActionButton
        title="Create a new submarket by specifying custom boundaries and filters for detailed market analysis."
        onClick={() => {
          setShowSubmarketCreationModal(true);
          setBoundarySelectionModalOpen(false);
        }}
        icon={FavoriteBorderIcon}
        label="Save Submarket"
        smallLabel={true}
        highlight={true}
      />
    </Box>
  );

  const ListingCountHeader = (
    <div>
      {/*
        When google translate is in used, if the components are rendered conditionally, it is causing page crash.
        Rendering both of the conponent, conditioning the style so because of any overridding component doesn't crash.
      */}
      <div style={{ ...(!isActiveListingCountHeaderLoading && { display: 'none' }) }}>
        {ActiveListingsCountHeaderLoading}
      </div>
      <div style={{ ...(isActiveListingCountHeaderLoading && { display: 'none' }) }}>
        {ActiveListingCountHeader}
      </div>
    </div>
  );

  const getGetNavHeader = (
    hideOption: string = '',
    extraStyling: any = {},
    disableInDesktop: boolean = false,
    size: string = '',
    disableRender: boolean = false
  ) =>
    disableInDesktop && !matches ? (
      <></>
    ) : (
      <Box
        sx={{
          overflowX: matches ? 'auto' : 'visible',
          overflowY: 'hidden',
          width: '100%',
          paddingRight: matches ? '20px' : 'auto',
        }}
      >
        <DashboardNavbar
          overviewDashboardTabs={overviewDashboardTabs}
          dashboardLayoutView={dashboardLayoutView}
          isMobile={matches}
          hideOption={hideOption}
          extraStyling={extraStyling}
          size={size}
        />
      </Box>
    );

  const shouldMainDashboardHeaderRender: boolean = ['dashboard', 'map', 'submarkets'].includes(
    dashboardLayoutView
  );

  const NavbarBreadcrumb = ({ parentMarketDetails }) => {
    const isVerifiedMarket = search.data?.isVerifiedMarket ?? false;
    if (!parentMarketDetails)
      return (
        <Breadcrumbs aria-label="breadcrumb" sx={{ pb: 0, ml: 0.5 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h5" className="marketTitle" color="grey">
              {marketDisplayName}
            </Typography>
            {isVerifiedMarket && <VerifiedTag size="small" />}
          </Box>
        </Breadcrumbs>
      );

    try {
      const country = parentMarketDetails.country
        ? parentMarketDetails.country.replace(/\s/g, '-').toLocaleLowerCase()
        : '0';
      const city = parentMarketDetails.city ? parentMarketDetails.city.toLocaleLowerCase() : '0';
      const district = parentMarketDetails.name.toLocaleLowerCase();

      const parentMarketUrl = `/airbnb-data/${country}/${city}/${district}?market_id=${parentMarketDetails.id}`;

      return (
        <Breadcrumbs aria-label="breadcrumb" sx={{ pb: 0, mb: 0, ml: 0.5 }}>
          <Link
            underline="hover"
            color="primary"
            href={parentMarketUrl}
            target="_blank"
            sx={{ fontSize: 'inherit' }}
          >
            {parentMarketDetails.displayName
              ? parentMarketDetails.displayName
              : parentMarketDetails.name}
          </Link>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Typography color="grey" variant="h5" className="marketTitle">
              {marketDisplayName}
            </Typography>
            {isVerifiedMarket && <VerifiedTag size="small" />}
          </Box>
        </Breadcrumbs>
      );
    } catch (err) {
      console.error('Error in breadcrumb generation: ', err);
      return <></>;
    }
  };

  const FilterCoingSoonComponent = (closeable: boolean = true) => {
    const text =
      'Filters are only available for markets with up to 15,000 listings. Coming soon to larger markets';
    return (
      <Grid item xs={12} sx={{ mt: 2, position: 'relative', zIndex: 1000 }}>
        {closeable ? (
          <Alert severity="warning" onClose={() => setShowFilterComingSoonAlert(true)}>
            {text}
          </Alert>
        ) : (
          <Alert severity="warning">{text}</Alert>
        )}
      </Grid>
    );
  };

  const FilterCacheLoadingComponent = (
    <Grid item xs={12} sx={{ mt: 2, position: 'relative', zIndex: 1000 }}>
      <Alert severity="warning">
        We're currently generating filter data for this market. In the meantime, the graphs show all
        listings. Filters will be available within 5 minutes.
      </Alert>
    </Grid>
  );

  const FilterBarComponent = (
    <>
      <Grid
        container
        item
        xs={12}
        direction="column"
        justifyContent="space-between"
        sx={{
          overflowX: 'auto',
          // the scroll bar should not be visible intitally, but should be visible when the user scrolls
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <FilterBar
          isMobileView={matches}
          dashboardLayoutView={dashboardLayoutView}
          filterVal={filterVal}
          setFilterVal={setFilterVal}
          priceTiers={priceTiers}
          currency={currency}
          resetFilter={resetFilter}
          setFilterApplied={setFilterApplied}
          revenuePotentialTiersDistribution={revPotentialRangeInvestment}
          handleOnSampleClick={handleOnSampleClick}
          showFilterComingSoonAlert={showFilterComingSoonAlert}
          isUserAllowed={isUserAllowed}
          FilterCoingSoonComponent={
            showFilterComingSoonAlert ? FilterCoingSoonComponent(false) : null
          }
        />
      </Grid>
      {showFilterComingSoonAlert === 'open' &&
        dashboardLayoutView === 'dashboard' &&
        FilterCoingSoonComponent()}

      {filterCacheLoading && FilterCacheLoadingComponent}
    </>
  );

  const shouldShowMapToggleBtn =
    dashboardLayoutView !== 'compare' && dashboardLayoutView !== 'submarkets' && !matches;

  const MapComponent = (
    <>
      <Map
        mapCenter={mapCenter}
        setMapCenter={setMapCenter}
        marketID={marketID}
        matches={matches}
        dashboardLayoutView={dashboardLayoutView}
        setDashboardLayoutView={setDashboardLayoutView}
        zoom={zoom}
        setZoom={setZoom}
        search={search}
        bounds={bounds}
        setBounds={setBounds}
        setIsDynamicDataBtnVisible={setIsDynamicDataBtnVisible}
        isDisabled={isDisabled}
        isUserAllowed={isUserAllowed}
        revPotentialRangeInvestment={revPotentialRangeInvestment}
        subscriptionModalOpen={subscriptionModalOpen}
        currency={currency}
        setSubscriptionModalOpen={setSubscriptionModalOpen}
        handleOnSampleClick={handleOnSampleClick}
        filterActive={filterActive}
        setShowPremiumDataDynamic={setShowPremiumDataDynamic}
        toggleDrawer={toggleDrawer}
        setFilterVal={setFilterVal}
        filterVal={filterVal}
        setFilterActive={setFilterActive}
        noDataInMarket={noDataInMarket}
        topPerformingListingOnFocus={topPerformingListingOnFocus}
        setListingsGridStartIndexOffset={setListingsGridStartIndexOffset}
        topPerformingMarketOnFocus={topPerformingMarketOnFocus}
        setTopPerformingMarketOnFocus={setTopPerformingMarketOnFocus}
        marketsToCompareList={marketsToCompareList}
        isTabChanging={isTabChanging}
        openDemoBookingForm={openDemoBookingForm}
        setDemoFormHeadingFor={setDemoFormHeadingFor}
        visibleMapListings={visibleMapListings}
        mapViewportListings={mapViewportListings}
        setSubmarketPolygonPaths={setSubmarketPolygonPaths}
        mapLoading={mapLoading}
        setMapLoading={setMapLoading}
        OnBoardingSearchBox={OnBoardingSearchBox}
        showViewingLisingFromPaginationAlert={showViewingLisingFromPaginationAlert}
        setShowViewingLisingFromPaginationAlert={setShowViewingLisingFromPaginationAlert}
        setMapHeightOnMobileView={setMapHeightOnMobileView}
        cachedMarketBedroomADR={cachedMarketBedroomADR}
        cachedMarketData={cachedMarketData.data}
        callDynamicMarkers={callDynamicMarkers}
        callBookingMarkers={callBookingMarkers}
        submarkets={submarkets}
        hoveredSubmarketId={hoveredSubmarketId}
        setHoveredSubmarketId={setHoveredSubmarketId}
        handleHideSubmarket={handleHideSubmarket}
        parentMarketDetails={parentMarketDetails}
        savedClusters={savedClusters}
        setSavedClusters={setSavedClusters}
        getSubSectionHeading={getSubSectionHeading}
        getMapViewHeadings={getMapViewHeadings}
        OverViewMapSectionMarketStatsTooltip={OverViewMapSectionMarketStatsTooltip}
        isDynamicDataBtnVisible={isDynamicDataBtnVisible}
        SaveSubMarketButton={SaveSubMarketButton}
        MainKpiMapView={MainKpiMapView}
        isMapCollapsed={isMapCollapsed}
        ListingCountHeader={ListingCountHeader}
        ListingCardGrid={ListingCardGrid}
        marketDisplayName={marketDisplayName}
        activeListingCountDataToUse={activeListingCountDataToUse}
        currentHoveredCardFromDataGrid={currentHoveredCardFromDataGrid}
        setCurrentHoveredCardFromDataGrid={setCurrentHoveredCardFromDataGrid}
        FilterBarComponent={FilterBarComponent}
        showFilterComingSoonAlert={showFilterComingSoonAlert}
        setShowFilterComingSoonAlert={setShowFilterComingSoonAlert}
        filterCacheLoading={filterCacheLoading}
        filterApplied={filterApplied}
        listingSortingKey={listingSortingKey}
        sx={dashboardLayoutView === 'submarkets' ? { borderRadius: '20px' } : {}}
        isSubmarketAccessAllowed={isSubmarketAccessAllowed}
        mapMode={mapMode}
        setMapMode={setMapMode}
        heatmapMode={heatmapMode}
        setHeatmapMode={setHeatmapMode}
        hiddenSubmarkets={hiddenSubmarkets}
        mapCopy={mapCopy}
        setMapCopy={setMapCopy}
        mapsCopy={mapsCopy}
        setMapsCopy={setMapsCopy}
        isNewSubmarketDrawingMode={isNewSubmarketDrawingMode}
        setIsNewSubmarketDrawingMode={setIsNewSubmarketDrawingMode}
        setTailoredSubmarketPolygon={setTailoredSubmarketPolygon}
        setShowSubmarketCreationModal={setShowSubmarketCreationModal}
        isLoggedIn={isLoggedIn}
        visibleBookingListings={visibleBookingListings}
        submarketSortKey={submarketSortKey}
        setShowFullSubmarketMap={setShowFullSubmarketMap}
        showFullSubmarketMap={showFullSubmarketMap}
        mobileHeaderHeight={mobileHeaderHeight}
        showSubmarketMobileLegend={showSubmarketMobileLegend}
        setShowSubmarketMobileLegend={setShowSubmarketMobileLegend}
        isFullOverviewMap={mapWidth == 75}
      />
      {shouldShowMapToggleBtn && (
        <Button
          variant="contained"
          className="mapToggleBtn"
          size="small"
          onClick={() => {
            if (mapWidth == 20) {
              setMapWidth(getSelectedViewWidth('map', matches));
              setDashboardLayoutView('map');
              logAmplitudeEvent('map_viewed_in_overview', { cta: 'toggle_btn' });
            } else if (
              (mapWidth == MAP_SIZE_MAP_VIEW_COLLAPSED_DESKTOP || mapWidth == 100) &&
              dashboardLayoutView === 'map'
            ) {
              setMapWidth(MAP_SIZE_MAP_VIEW_DESKTOP);
            } else if (mapWidth == MAP_SIZE_MAP_VIEW_DESKTOP && dashboardLayoutView === 'map') {
              setMapWidth(MAP_SIZE_MAP_VIEW_COLLAPSED_DESKTOP);
            }
          }}
        >
          <ArrowRightRoundedIcon
            className={`mapToggleBtn__icon ${
              dashboardLayoutView === 'map' && mapWidth >= MAP_SIZE_MAP_VIEW_DESKTOP
                ? 'mapToggleBtn__icon--active'
                : ''
            }`}
          />
        </Button>
      )}
    </>
  );

  const MarketHeaderComponent = () => {
    return (
      <Box
        className="subsection--spacing"
        sx={{
          pt: 2,
          width: '100vw',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Grid item xs={10}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            <NavbarBreadcrumb parentMarketDetails={parentMarketDetails} />
            {dashboardLayoutView === 'submarkets' && matches && (
              <TooltipNew
                title=""
                content="Click to see the 3 minutes tutorial video on how to use this feature."
              >
                <IconButton color="primary" onClick={() => setShowSubmarketTutorial(true)}>
                  <HelpOutlineRoundedIcon />
                </IconButton>
              </TooltipNew>
            )}
          </Box>
        </Grid>
        <Grid container item xs={2} justifyContent="flex-end" alignItems="center" sx={{ pr: 1 }}>
          {dashboardLayoutView === 'dashboard' && matches && (
            <Grid item>
              <IconButton
                onClick={
                  !isPDFDataToDownloadLoading || user.subscriptionPlan === 'starter'
                    ? () => {
                        setShowExportAsPDFModal(true);
                      }
                    : () => {}
                }
                sx={{
                  p: 0,
                  m: 0,
                }}
              >
                {isPDFDataToDownloadLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
              </IconButton>
            </Grid>
          )}
          <Grid item>
            <ActionOptions
              setShowListingsExportPage={setShowListingsExportPage}
              handleOnSampleClick={handleOnSampleClick}
              setShowSubmarketCreationModal={setShowSubmarketCreationModal}
              marketID={marketID}
              marketName={marketDisplayName}
              setUserRemovedThisMarketFromOverview={setUserRemovedThisMarketFromOverview}
              isLoggedIn={isLoggedIn}
              setShowExportAsPDFModal={setShowExportAsPDFModal}
              isPDFDataToDownloadLoading={isPDFDataToDownloadLoading}
              showExportDashboardOption={matches ? false : true}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  const MobileHeaderComponent = (
    <Grid
      ref={mobileHeaderRef}
      container
      item
      xs={12}
      className="overviewPage__header--sticky"
      sx={{ width: '95%' }}
    >
      {/* navbar */}
      {shouldMainDashboardHeaderRender && getGetNavHeader()}
      {/* market header and action button */}
      {['dashboard', 'map', 'submarkets'].includes(dashboardLayoutView) && (
        <MarketHeaderComponent />
      )}
      {/* filter bar */}
      {(dashboardLayoutView === 'dashboard' || dashboardLayoutView === 'map') && (
        <Box className="subsection--spacing" sx={{ maxWidth: '100%' }}>
          {FilterBarComponent}
        </Box>
      )}
    </Grid>
  );

  const isCacheStillLoading =
    cachedMarketData.loading && cachedMarketData.data == null;
  return isLoggedIn &&
    (dashboardLayoutView === 'dashboard' || dashboardLayoutView === 'map') &&
    (isMarketOverviewLoading || isCacheStillLoading) &&
    !isTailoredRegionOpen ? (
    <FullScreenLoader />
  ) : (
    <>
      {/* title and meta tags */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {`Airbnb Data on in ${titleCase(
            SEODistrictName
          )} | Airbtics | ${SEOCountryName}${SEOCityName}`}
        </title>
        <meta
          name="description"
          content={`Occupancy rate, daily rate, and monthly revenue data in ${SEODistrictName}. Here's all your short-term rental data to invest Airbnb smarter! `}
        />
      </Helmet>

      {!isMarketSearchAllowed(user, search.data?.id, dummyMarket.id, true) &&
        ['dashboard', 'map', 'submarkets'].includes(dashboardLayoutView) &&
        !isUserAllowed && (
          <ViewDemoButton
            handleClick={() => handleOnSampleClick('expert')}
            buttonText={`You’ve reached your limit of ${FREE_MARKET_SEARCH_LIMIT} free searches. Subscribe to unlock unlimited searches.`}
            topOffsetMobile="30%"
          />
        )}

      {search.data?.isSubmarket && (
        <SubmarketToTailoredRegion
          search={search}
          getTokenThunk={getTokenThunk}
          setIsTailoredRegionOpen={true}
          tailoredRegionMarkers={tailoredRegionMarkers}
          getTailoredRegionMarkers={getTailoredRegionMarkers}
        />
      )}

      <Grid container direction="row" className="dashboard__overview" item xs={12}>
        {/* SEO */}
        {search.data && search.data.marketBlogLink && (
          <a
            href={search.data.marketBlogLink}
            className="offscreen"
            style={{ opacity: 0, height: 0 }}
          >
            {' '}
            {`See annual Airbnb revenue in ${search.data.marketBlogLink.split('/').pop()}`}{' '}
          </a>
        )}

        {cachedMarketData.isRateLimited && (
          <Grid container>
            <Grid item xs={12} sx={{ p: '20px'}}>
              <RateLimitAlert />
            </Grid>
          </Grid>
        )}

        {!cachedMarketData.isRateLimited && (
          <Grid
            container
            alignItems={'flex-start'}
            sx={{ width: mapWidth + '%', overflow: 'hidden' }}
            className="leftView"
            id="overviewLeft"
          >
            <Paper id="map" className="mapWrapper">
              {dashboardLayoutView === 'map' && matches && (
                <Box sx={{ pl: '15px' }}>{MobileHeaderComponent}</Box>
              )}
              {!search.loading &&
                mapWidth !== 0 &&
                dashboardLayoutView !== 'dashboard' &&
                MapComponent}
            </Paper>
          </Grid>
        )}

        {!cachedMarketData.isRateLimited && (
          <Grid
            container
            sx={{
              width: 100 - mapWidth + '%',
            }}
            className="rightView"
            id="overviewRight"
          >
            <Grid
              container
              sx={{
                ...(dashboardLayoutView === 'dashboard' && {
                  ...theme.utils.container,
                }),
              }}
            >
              <Grid
                container
                justifyContent={'flex-start'}
                alignItems={'flex-start'}
                direction={'row'}
                item
                xs={12}
                className="rightView__container"
              >
                {!matches &&
                  (dashboardLayoutView === 'dashboard' ||
                    dashboardLayoutView === 'map' ||
                    dashboardLayoutView === 'compare') && <></>}

                {/* Mobile only */}
                {matches && MobileHeaderComponent}

                {/* Desktop only */}
                {!matches && (
                  <Grid container>
                    {/* market header and action button */}
                    {['dashboard', 'map', 'submarkets'].includes(dashboardLayoutView) && (
                      <MarketHeaderComponent />
                    )}

                    {/* navbar */}
                    <Grid item xs={12} className="subsection--spacing" sx={{ position: 'sticky' }}>
                      {shouldMainDashboardHeaderRender && getGetNavHeader()}
                    </Grid>

                    {/* filter bar */}
                    {(dashboardLayoutView === 'dashboard' || dashboardLayoutView === 'map') && (
                      <Box className="subsection--spacing" sx={{ maxWidth: '100%' }}>
                        {FilterBarComponent}
                      </Box>
                    )}

                    {/* divider */}
                    {(dashboardLayoutView === 'dashboard' || dashboardLayoutView === 'map') && (
                      <Grid item xs={12} className="subsection--spacing">
                        <Divider variant="fullWidth" />
                      </Grid>
                    )}
                  </Grid>
                )}

                {!isTabChanging && dashboardLayoutView === 'map' && !matches ? (
                  <Grid container item xs={12}>
                    {ListingCountHeader}

                    <Grid
                      container
                      item
                      sx={{
                        marginBottom: '10px',
                        borderRadius: '8px',
                        paddingTop: '10px',
                      }}
                    >
                      {ListingCardGrid}
                    </Grid>

                    <Grid
                      container
                      item
                      sx={{
                        height: '190px',
                        width: '100%',
                        marginBottom: '10px',
                        padding: '5px',
                        paddingBottom: '40px',
                        boxShadow: '0 4px 10px #0002, 0 -4px 10px -4px #0001',
                        borderRadius: '8px',
                        position: matches ? 'relative' : 'sticky',
                        bottom: 0,
                        background: '#fff',
                        overflow: 'scroll',
                      }}
                    >
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        sx={{ width: '100%', paddingLeft: '10px', paddingTop: '10px' }}
                      >
                        <Grid item xs={10}>
                          {getMapViewHeadings(
                            'Map Area Overview',
                            search.data ? marketDisplayName : '',
                            OverViewMapSectionMarketStatsTooltip
                          )}
                        </Grid>
                        <Grid item xs={2}>
                          {SaveSubMarketButton}
                        </Grid>
                      </Grid>
                      {MainKpiMapView(isMapCollapsed)}
                    </Grid>
                  </Grid>
                ) : (
                  <></>
                )}

                {isTabChanging && dashboardLoadingScreenFullSize}

                {dashboardLayoutView === 'dashboard' && !isTabChanging && (
                  <Grid
                    container
                    item
                    xs={12}
                    sx={{
                      pt: matches ? `${mobileHeaderHeight}px` : '0px',
                    }}
                  >
                    {/*
                        Entire Market Summary Tab starts from here.
                      */
                    noDataInMarket ? (
                      <Grid item xs={12} sx={{}}>
                        <Alert severity="warning">
                          <strong>Limited data available:</strong> This market currently has too few
                          active listings to generate fully reliable insights. Try expanding the
                          area or adjusting your filters to improve coverage.
                        </Alert>
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12} className="section--spacing">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <Typography
                              variant="h1"
                              className="marketTitle"
                              id="overview-tab"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                              }}
                            >
                              <>
                                {marketGrade.is404Error ? (
                                  <>{marketDisplayName} - Market Overview</>
                                ) : (
                                  <>
                                    Is {marketDisplayName} a good short-term rental market?
                                    <TooltipNew content="Click to see the 3 minutes tutorial video on how to use this feature.">
                                      <IconButton
                                        color="primary"
                                        onClick={() => setShowTutorial(true)}
                                      >
                                        <HelpOutlineRoundedIcon />
                                      </IconButton>
                                    </TooltipNew>
                                  </>
                                )}
                              </>
                            </Typography>
                          </Box>
                          <Box className="subsection--spacing">
                            <MarketGradeAIOverview
                              handleOnSampleClick={handleOnSampleClick}
                              setMarketGradeInfo={setMarketGradeInfo}
                            />
                          </Box>
                          <Box>{MainKPICardMemoed}</Box>
                        </Grid>
                        <Grid container item xs={12} className="section--spacing">
                          <SubmarketCTA
                            parentMarketID={parentMarketID ? parentMarketID : search.data.id}
                            currentMarketID={search.data.id}
                            marketDisplayName={marketDisplayName}
                            isMobileView={matches}
                            handleOnSampleClick={handleOnSampleClick}
                            submarkets={submarkets}
                            setDashboardLayoutView={setDashboardLayoutView}
                            isSubmarketAccessAllowed={isSubmarketAccessAllowed}
                            submarketSummary={submarketSummary}
                            parentMarketDetails={parentMarketDetails}
                            submarketSortKey={submarketSortKey}
                            isSubmarket={false}
                            submarketParentMarketPerformance={submarketParentMarketPerformance}
                            source="overview"
                            isSubmarketRateLimited={isSubmarketRateLimited}
                            filterApplied={filterApplied}
                          />
                        </Grid>

                        <Grid item xs={12} className="section--spacing">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <Typography
                              variant="h1"
                              className="marketTitle"
                              id="listings-tab"
                            >
                              What type of property works the best?
                            </Typography>
                            {filterApplied && (
                              <Tooltip title={MARKET_WIDE_BADGE_TOOLTIP} arrow placement="top">
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.375,
                                    px: 1.25,
                                    py: 0.375,
                                    borderRadius: 10,
                                    backgroundColor: COLOR_PRIMARY_2,
                                    color: '#fff',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    cursor: 'help',
                                  }}
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: '0.875rem' }} />
                                  Filter not applied
                                </Box>
                              </Tooltip>
                            )}
                          </Box>

                          <Box className="subsection--spacing">
                            <BestPropertyTypeOverview
                              marketDisplayName={marketDisplayName}
                              isMobileView={matches}
                              handleOnSampleClick={handleOnSampleClick}
                              data={cachedMarketData.data?.occupancy_by_best_property_types}
                              marketMedianOccupancyRate={marketLevelOccupancyRate}
                              isLoading={cachedMarketData.loading}
                              currency={currency}
                              isUserAllowed={isUserAllowed}
                              amenitiesImpactOnPropertyTypes={
                                cachedMarketData?.data?.revenue_breakdown
                                  ?.revenue_impact_on_amenities
                              }
                              isUSMarket={isUSMarket}
                              onOpenRealtorModal={() => setRealtorModalOpen(true)}
                            >
                              <Box className="subsection--spacing" sx={{ mt: 3 }}>
                                <ConfigurableFilterBarCard
                                  embedded
                                  isLoading={cachedMarketData.loading}
                                  filteredListingsData={filteredListingsDashboard.data}
                                  cachedMonthlyLTMData={cachedMonthlyLTMData}
                                  currency={currency}
                                  isDisabled={isDisabled}
                                  handleOnSampleClick={handleOnSampleClick}
                                  isCacheLoading={cachedMarketData.loading}
                                  bestPropertyTypeGraphRef={bestPropertyTypeGraphRef}
                                  configurableFilterBarCardSelectionModalRef={
                                    configurableFilterBarCardSelectionModalRef
                                  }
                                  setSelectedGraphExportDetails={setSelectedGraphExportDetails}
                                />
                              </Box>
                              <Box className="subsection--spacing">
                                <AmenitiesImpactOnRevenueChart
                                  embedded
                                  revenueImpactOnAmenitiesDistro={
                                    cachedMarketData?.data?.revenue_breakdown
                                      ?.revenue_impact_on_amenities
                                  }
                                  isDisabled={isDisabled}
                                  userTailorMarketAccess={userTailorMarketAccess}
                                  handleOnSampleClick={reqPlan =>
                                    handleOnSampleClick(reqPlan, 'revenueByAmenities')
                                  }
                                  isFilterActive={filterActive}
                                  noDataInMarket={noDataInMarket}
                                  cachedMarketData={cachedMarketData}
                                  demandByAmenitiesChartRef={demandByAmenitiesChartRef}
                                />
                              </Box>
                            </BestPropertyTypeOverview>
                          </Box>

                        </Grid>

                        <Grid item xs={12} className="section--spacing">
                          <MarketIntelligenceTab
                            filterApplied={filterApplied}
                            cachedMarketDataWithoutFilters={cachedMarketDataWithoutFilters}
                            shortTermRentalSection={
                              <>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    mb: 3,
                                  }}
                                >
                                  <Typography variant="h1" className="marketTitle">
                                    What Do Airbnb Listings Look Like in {marketDisplayName ?? 'this market'}?
                                  </Typography>
                                  <AirbnbBadge tooltipTitle="Property characteristics and listing counts are based on Airbnb supply in this market." />
                                </Box>
                                <Box className="subsection--spacing">
                                  <AIOverviewCard isLoading={false} isMobileView={matches}>
                                    <Grid
                                      container
                                      direction="row"
                                      xs={12}
                                      spacing={1}
                                      justifyContent="flex-start"
                                      alignItems="flex-start"
                                    >
                                      <Grid
                                        item
                                        xs={matches ? 12 : 6}
                                        sx={{
                                          ...theme.utils.cardGap,
                                          display: 'flex',
                                          flexDirection: 'column',
                                        }}
                                      >
                                        {PropertySizeCardMemoed}
                                        {AmenityCardMemoed}
                                      </Grid>
                                      <Grid
                                        item
                                        xs={matches ? 12 : 6}
                                        sx={{
                                          ...theme.utils.cardGap,
                                          display: 'flex',
                                          flexDirection: 'column',
                                        }}
                                      >
                                        {PropertyTypeChannelCancelationPolicyPieChat}
                                        {ActiveDaysFilterCardSingleBar}
                                      </Grid>
                                      <Grid item xs={12}>
                                        {CapacityFilterCardSingleBar}
                                      </Grid>
                                    </Grid>
                                  </AIOverviewCard>
                                </Box>
                              </>
                            }
                            marketDisplayName={marketDisplayName}
                            isMobileView={matches}
                            handleOnSampleClick={handleOnSampleClick}
                            cachedMarketData={cachedMarketData}
                            topHosts={topHosts}
                            currency={currency}
                            user={user}
                            matches={matches}
                            searchObj={search.data}
                            isUserAllowed={isUserAllowed}
                            warningTextComponent={ComponentFilterSupportComingSoonText()}
                            setSelectedGraphExportDetails={setSelectedGraphExportDetails}
                            domesticVsInternationalGraphRef={domesticVsInternationalGraphRef}
                            noDataInMarket={noDataInMarket}
                            userTailorMarketAccess={userTailorMarketAccess}
                            isDisabled={isDisabled}
                            dashboardLayoutView={dashboardLayoutView}
                            isDemoMarket={isDemoMarket}
                            dailyOccupancyRateCalendarHistoricalGraphRef={
                              dailyOccupancyRateCalendarHistoricalGraphRef
                            }
                            dailyOccupancyRateCalendarFutureGraphRef={
                              dailyOccupancyRateCalendarFutureGraphRef
                            }
                            calendarMarketDisplayName={
                              parentMarketDetails ? parentMarketDisplayName : marketDisplayName
                            }
                            calendarMarketId={
                              parentMarketDetails ? parentMarketDetails.id : search.data.id
                            }
                            onRequestHardRefresh={handleRequestHardRefresh}
                          />
                        </Grid>

                        {/* Guests section moved to Market Intelligence tab - block hidden */}
                        <Grid item xs={12} className="section--spacing" sx={{ display: 'none' }}>
                          <Typography
                            variant="h1"
                            className="marketTitle subsection--spacing"
                            id="guests-tab"
                          >
                            Who’s staying in Airbnbs here?
                          </Typography>

                          <Box className="subsection--spacing">
                            {InternationDomesticGuestsCardMemoed}
                          </Box>

                          <Box className="subsection--spacing">
                            <Grid
                              item
                              container
                              direction={'row'}
                              xs={12}
                              spacing={1}
                              justifyContent={'flex-start'}
                              alignItems={'flex-start'}
                            >
                              <Grid item xs={matches ? 12 : 4}>
                                {TopCitiesCardMemoed}
                              </Grid>
                              <Grid item xs={matches ? 12 : 4}>
                                {TopCountriesCardMemoed}
                              </Grid>
                              <Grid item xs={matches ? 12 : 4}>
                                {GuestLanguageCardMemoed}
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>

                        <Grid
                          container
                          item
                          xs={12}
                          justifyContent={'center'}
                          alignItems={'flex-start'}
                          className="section--spacing"
                        >
                          <Button
                            onClick={() => {
                              setDashboardLayoutView('map');
                            }}
                            sx={{
                              background: COLOR_PRIMARY_1,
                              color: '#fff',
                              padding: '15px 30px',
                              transition: 'all 0.25s ease',
                              cursor: 'pointer',
                              fontSize: '20px',
                              '&:hover': {
                                background: COLOR_PRIMARY_1 + '44',
                                color: COLOR_PRIMARY_1,
                              },
                            }}
                          >
                            Analyze Listings in the map
                          </Button>
                        </Grid>

                        {isUSMarket && (
                          <Grid
                            container
                            item
                            xs={12}
                            justifyContent={'center'}
                            alignItems={'flex-start'}
                            className="section--spacing"
                            sx={{ mt: '20px', mb: '40px' }}
                          >
                            <RealtorForm
                              location={marketDisplayName || ''}
                              open={realtorModalOpen}
                              onClose={() => setRealtorModalOpen(false)}
                              onOpen={() => setRealtorModalOpen(true)}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                  /*
                  Entire Market Summary Tab ends here.
                */
                )}

                {dashboardLayoutView === 'submarkets' && (
                  <SubmarketsTab
                    key={'submarkets'}
                    currency={currency}
                    submarkets={submarkets}
                    subscriptionPlan={user.subscriptionPlan}
                    handleOnSampleClick={handleOnSampleClick}
                    isMobileScreen={matches}
                    setVisibleSubmarkets={setVisibleSubmarkets}
                    hoveredSubmarketId={hoveredSubmarketId}
                    setHoveredSubmarketId={setHoveredSubmarketId}
                    visibleSubmarkets={visibleSubmarkets}
                    handleHideSubmarket={handleHideSubmarket}
                    marketID={parentMarketID ? parentMarketID : search.data.id}
                    hiddenSubmarkets={hiddenSubmarkets}
                    parentMarketDetails={parentMarketDetails}
                    setShowSubmarketCreationModal={setShowSubmarketCreationModal}
                    setSubmarketCreationSource={setSubmarketCreationSource}
                    MapComponent={MapComponent}
                    submarketSortKey={submarketSortKey}
                    setSubmarketSortKey={setSubmarketSortKey}
                    fetchSubMarketData={fetchSubMarketData}
                    setSubmarkets={setSubmarkets}
                    submarketBedroomType={submarketBedroomType}
                    setSubmarketBedroomType={setSubmarketBedroomType}
                    isSubmarketAccessAllowed={isSubmarketAccessAllowed}
                    setIsSubmarketAccessAllowed={setIsSubmarketAccessAllowed}
                    setMapCenter={setMapCenter}
                    mapMode={mapMode}
                    heatmapMode={heatmapMode}
                    createSubmarketHideUnhideNotification={createSubmarketHideUnhideNotification}
                    mapCopy={mapCopy}
                    mapsCopy={mapsCopy}
                    newlyAddedSubmarketId={newlyAddedSubmarketId}
                    setNewlyAddedSubmarketId={setNewlyAddedSubmarketId}
                    isNewSubmarketDrawingMode={isNewSubmarketDrawingMode}
                    setIsNewSubmarketDrawingMode={setIsNewSubmarketDrawingMode}
                    showFullSubmarketMap={showFullSubmarketMap}
                    setSelectedGraphExportDetails={setSelectedGraphExportDetails}
                    mobileHeaderHeight={mobileHeaderHeight}
                    showTutorial={showSubmarketTutorial}
                    setShowTutorial={setShowSubmarketTutorial}
                    setShowSubmarketMobileLegend={setShowSubmarketMobileLegend}
                    isSubmarketRateLimited={isSubmarketRateLimited}
                  />
                )}

                {dashboardLayoutView === 'compare' && (
                  <ExplorerPagePage
                    currency={currency}
                    handleOnSampleClick={handleOnSampleClick}
                    isMobileScreen={matches}
                    OnBoardingSearchBox={OnBoardingSearchBox}
                    isLoggedIn={isLoggedIn}
                    handleSidebarClick={handleMarketSidebarClick}
                    handleSidebarListItemClick={handleSidebarListItemClick}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      {topPerformingListingModalOpen && (
        <IndividualListingModalV2
          open={true}
          onClose={() => setTopPerformingListingModalOpen(null)}
          data={topPerformingListingModalOpen}
          currency={currency}
          isDisabled={isDisabled}
          setSubscriptionModalOpen={setSubscriptionModalOpen}
          handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'individualListingsKPI')}
          subscriptionModalOpen={subscriptionModalOpen}
          isEstimator={false}
          openDemoBookingForm={openDemoBookingForm}
          setDemoFormHeadingFor={setDemoFormHeadingFor}
          referenceADR={
            cachedMarketBedroomADR
              ? cachedMarketBedroomADR[
                  (topPerformingListingModalOpen as Listing)?.bedrooms ||
                    (topPerformingListingModalOpen as Listing)?.bedrooms
                ]
              : null
          }
          cachedMarketData={cachedMarketData.data}
          marketID={marketID}
        />
      )}
      {showListingsExportPage && (
        <ListingsExportModal
          open={showListingsExportPage}
          setOpen={setShowListingsExportPage}
          matches={matches}
          dataStoreTimePeriodQuery={dataStoreTimePeriodQuery}
          dataStoreTimeGranularityQuery={dataStoreTimeGranularityQuery}
          isLoggedIn={isLoggedIn}
          setLoginModalOpen={setLoginModalOpen}
        />
      )}

      {showSubmarketCreationModal && (
        <SubmarketCreationModal
          open={showSubmarketCreationModal}
          setOpen={setShowSubmarketCreationModal}
          setSubmarketPolygonPaths={setSubmarketPolygonPaths}
          setBoundarySelectionModalOpen={setBoundarySelectionModalOpen}
          submarketCreationSource={submarketCreationSource}
          parentMarketID={parentMarketID ? parentMarketID : search.data.id}
          setNewlyAddedSubmarketId={setNewlyAddedSubmarketId}
          fetchSubMarketData={fetchSubMarketData}
          tailoredSubmarketPolygon={tailoredSubmarketPolygon as any}
          setTailoredSubmarketPolygon={setTailoredSubmarketPolygon}
        />
      )}

      {showListingsExportPaymentFulfillmentCheckModal && (
        <PaymentFulfillmentCheckModal
          open={showListingsExportPaymentFulfillmentCheckModal}
          setOpen={setShowListingsExportPaymentFulfillmentCheckModal}
          matches={matches}
          status={listingsExportCheckoutStatus}
          successMessage="Your dataset will be prepared within 15 minutes. You'll receive it via email and can also download it from Your Account page."
          failureMessage="Please try again."
        />
      )}
      {extraExportCreditsLoading && (
        <Backdrop
          sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
          open={extraExportCreditsLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <FloatingClusterButton
        marketID={marketID}
        currency={currency}
        savedClusters={savedClusters}
        setSavedClusters={setSavedClusters}
        isUserAllowed={isUserAllowed}
      />

      {dashboardLayoutView === 'map' && (
        <Modal
          open={showFilterComingSoonAlert === 'open'}
          onClose={() => setShowFilterComingSoonAlert(false)}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
            }}
          >
            {FilterCoingSoonComponent()}
          </Box>
        </Modal>
      )}

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {showExportAsPDFModal && (
        <ExportMarketPdfModal
          open={showExportAsPDFModal}
          setOpen={setShowExportAsPDFModal}
          currency={currency}
          handleOnSampleClick={handleOnSampleClick}
          marketDetails={{
            id: search.data.id,
            name: marketDisplayName,
            city: search.data.city,
            country: search.data.country,
            countryCode: search.data.countryCode,
          }}
          marketGradeInfo={
            marketGrade.loading ||
            marketGrade.is404Error ||
            marketGrade.isError ||
            !marketGrade.data?.market_grade
              ? null
              : marketGradeInfo
          }
          marketDataCache={cachedMarketData.data}
          marketTrend={nextYearPredcition}
          marketSeasonality={getSeasonalityBarColor(
            getSeasonality(revenueHistoricalData, 'overview', selectedPercentileInMainKPICard)[
              'seasonality'
            ],
            true
          )}
          revenueFormattedChartData={revenueFormattedChartData}
          formattedSubmarketSummary={formattedSubmarketSummary.current}
          top3SubmarketsRef={top3SubmarketsRef.current}
          submarketSortKey={SUBMARKET_SORTING_OPTIONS[submarketSortKey]?.name}
          guestGraphData={cachedMarketData.data?.guest_origin}
          isPdfDownloading={isPdfDownloading}
          setIsPdfDownloading={setIsPdfDownloading}
          handleExportDashboardPDF={handleExportDashboardPDF}
          topSubMarketChartRef={topSubMarketChartRef}
          demandByAmenitiesChartRef={demandByAmenitiesChartRef}
          mainKpiRevenueChartRef={mainKpiRevenueChartRef}
          bestPropertyTypeGraphRef={bestPropertyTypeGraphRef}
          listingByPropertySizeGraphRef={listingByPropertySizeGraphRef}
          listingByPropertyTypeGraphRef={listingByPropertyTypeGraphRef}
          cancellationPolicyGraphRef={cancellationPolicyGraphRef}
          listingsByDaysAvailableGraphRef={listingsByDaysAvailableGraphRef}
          listingsByCapacityGraphRef={listingsByCapacityGraphRef}
          domesticVsInternationalGraphRef={domesticVsInternationalGraphRef}
          monthlyAlosDataGraphRef={monthlyAlosDataGraphRef}
          monthlyBltDataGraphRef={monthlyBltDataGraphRef}
          occupancyRateCalendarDataHistorical={
            cachedMarketData?.data?.historical_occupancy_calendar ?? {}
          }
          occupancyRateCalendarDataPrelim={{}}
          occupancyRateCalendarDataFuture={{}}
          dailyOccupancyRateCalendarHistoricalGraphRef={
            dailyOccupancyRateCalendarHistoricalGraphRef
          }
          dailyOccupancyRateCalendarFutureGraphRef={dailyOccupancyRateCalendarFutureGraphRef}
          filterVal={filterVal}
          filterActive={filterActive}
          isSubmarket={search.data.isSubmarket}
          isFullHostUnlocked={topHosts.isUserAllowedToHostData}
          isPDFDataToDownloadLoading={isPDFDataToDownloadLoading}
          reportMarketHeader={
            marketGrade.is404Error
              ? `${marketDisplayName} - Market Overview`
              : `Is ${marketDisplayName} a good short-term rental market?`
          }
          configurableFilterBarCardSelectionModalRef={configurableFilterBarCardSelectionModalRef}
        />
      )}

      {downloadPDFUrl && (
        <PDFDownloadModal downloadPDFUrl={downloadPDFUrl} setDownloadPDFUrl={setDownloadPDFUrl} />
      )}

      <GraphExportModal
        open={Boolean(selectedGraphExportDetails)}
        handleClose={() => setSelectedGraphExportDetails(null)}
        selectedGraphExportDetails={selectedGraphExportDetails}
        setSelectedGraphExportDetails={setSelectedGraphExportDetails}
        handleOnSampleClick={handleOnSampleClick}
        isCacheLoading={isPDFDataToDownloadLoading}
      />

      {showTutorial && (
        <TutorialModal
          open={showTutorial}
          onClose={() => setShowTutorial(false)}
          type="overview_page"
          noProgressBar={true}
        />
      )}
    </>
  );
};

const DashboardNavbar = ({
  overviewDashboardTabs,
  dashboardLayoutView,
  isMobile,
  hideOption,
  extraStyling,
  size,
}: {
  overviewDashboardTabs: {
    label: string | JSX.Element;
    value: string;
    icon: JSX.Element;
    onclick: () => void;
  }[];
  dashboardLayoutView: 'map' | 'dashboard' | 'compare' | 'submarkets';
  isMobile: boolean;
  hideOption?: string;
  extraStyling?: string;
  size?: string;
}) => {
  const renderTabLabelCondition = () => {
    return true;
  };

  return (
    <>
      <TabBar
        tabList={overviewDashboardTabs}
        selectedCurrentTab={dashboardLayoutView}
        color={COLOR_PRIMARY_2}
        renderLabelCondition={renderTabLabelCondition}
        actionButton={[]}
        showActionButton={dashboardLayoutView === 'map'}
        isMobile={isMobile}
        hideOption={hideOption}
        extraStyling={extraStyling}
        size={size}
      />
    </>
  );
};

export default OverviewPage;
